/**
 * API Client for M365 Security Investigation Portal
 *
 * All functions communicate with the Azure Functions backend.
 * In development, Vite proxies /api requests to http://localhost:7071.
 *
 * NOTE: Mock-data fallback was removed in v1.57.0 (MSP separation Phase 2).
 * The `mockApi` proxy below throws on any access so we catch any stray usage
 * during the refactor; remove the proxy and the `withMockFallback` shim once
 * we're confident no callers depend on the old behavior.
 */

import { getApiToken, forceRelogin } from '../hooks/useAuth';
import { nonInteractiveCollected } from '../utils/eventUtils';
import { isDemoMode, DemoReadOnlyError } from '../utils/demoMode';
import {
  anonymizeApiResponse,
  anonymizeDeep,
  scrubFreeText,
  restoreEndpoint,
  restoreBody,
} from '../utils/demoAnonymizer';
import { normalizeIdentityResolution } from '../utils/identityResolution';
import { normalizeTeamsRecords } from '../utils/teamsMeetings';
import { demoteGuessedIP } from '../utils/guessedIp';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const RULEBUILDER_BASE_URL = import.meta.env.VITE_RULEBUILDER_API_BASE_URL || '';

// Stub mock proxy. Mock data file was deleted in v1.57.0; this Proxy stands in
// for the old `import { mockApi } from './mockData'` so any inadvertently-left
// `mockApi.<x>()` reference throws a loud, recognizable error in dev.
const mockApi = new Proxy({}, {
  get(_target, prop) {
    return () => {
      throw new Error(`Mock data was removed in v1.57.0; mockApi.${String(prop)} is no longer available.`);
    };
  },
});

// Raise the global connection-error banner (ConnectionErrorBanner listens for this).
// Fired when a request fails at the network layer ("Failed to fetch") — which never
// reaches the HTTP 401 auto-relogin path below.
function notifyConnectionError() {
  if (typeof window !== 'undefined') {
    try { window.dispatchEvent(new CustomEvent('api:connection-error')); } catch { /* no-op */ }
  }
}

const CONNECTION_ERROR_MSG =
  "Couldn't reach the server — the connection dropped before it responded. This is usually temporary; wait a moment and try again, or reload the page.";

// ---------------------------------------------------------------------------
// Demo Mode (see utils/demoMode.js + utils/demoAnonymizer.js)
//
// Read once at module scope — toggling Demo Mode always reloads the page, so
// this can never drift from the stored flag.
// ---------------------------------------------------------------------------
const DEMO = isDemoMode();

// POST-shaped endpoints that are genuinely READ-ONLY pivots (pure Databricks /
// ATS queries with no writes and no metered spend). These stay usable in Demo
// Mode; their JSON bodies carry masked tenant IDs / IPs / UPNs, so the body is
// run through the key store on the way out.
//
// Everything else that mutates is blocked — including button-shaped calls that
// look like reads but aren't: refreshIpEnrichment / refresh-batch (ProxyCheck
// quota + Delta MERGE + alert-row write), resolveDirectoryName and
// recheck-signin / link-source-event (persist onto the alert row),
// subject-view (audit write, already fail-soft), test runs and backfills.
const DEMO_READ_POSTS = [
  /^\/api\/alerts\/[^/?]+\/session-trace(\?|$)/,
  /^\/api\/alerts\/[^/?]+\/token-use(\?|$)/,
  /^\/api\/cases\/[^/?]+\/ip-evidence(\?|$)/,
];

// Render an API error body's `errors` array as one readable sentence.
//
// Most endpoints put plain strings here (CreateRule, RequestExecutiveReport,
// GetRunbookStatus). RefreshIpEnrichmentBatch puts objects — `{ ip, message }` —
// which is the better contract for a caller that wants structure, but a bare
// `.join('; ')` renders each one as "[object Object]". That is what the case IP
// table showed for a batch enrich whose IPs were all rejected: the message named
// the failure but hid the seven values that caused it, i.e. it hid the only part
// an analyst could act on. Keep both shapes readable, and keep the line bounded —
// this array can carry one entry per requested IP (up to 500).
const ERROR_LIST_MAX = 8;
function formatErrorList(errors) {
  const parts = errors.slice(0, ERROR_LIST_MAX).map((e) => {
    if (e == null) return '';
    if (typeof e === 'string') return e;
    if (typeof e !== 'object') return String(e);
    // Name the subject when the entry carries one, so the reader learns WHICH
    // item failed and not just that something did.
    const subject = e.ip || e.id || e.name || e.field || e.target || '';
    const detail  = e.message || e.error || e.reason || e.details || '';
    if (subject && detail) return `${subject} (${detail})`;
    if (subject || detail) return String(subject || detail);
    try { return JSON.stringify(e); } catch { return String(e); }
  }).filter(Boolean);
  if (parts.length === 0) return '';
  const hidden = Math.max(0, errors.length - ERROR_LIST_MAX);
  return parts.join('; ') + (hidden > 0 ? ` … and ${hidden} more` : '');
}

/**
 * Generic fetch wrapper with error handling.
 */
async function request(endpoint, options = {}) {
  // Destructure private options, pass rest to fetch
  const { headers: optionHeaders, _returnResponse, _baseUrl, _responseType, ...fetchOptions } = options;
  const baseUrl = _baseUrl || BASE_URL;

  // Demo Mode: block writes, then restore real request keys from the masked /
  // fake values the UI is displaying. Must run BEFORE the URL is built.
  if (DEMO) {
    const demoMethod = (fetchOptions.method || 'GET').toUpperCase();
    const isReadPost = DEMO_READ_POSTS.some((re) => re.test(endpoint));
    if (demoMethod !== 'GET' && !isReadPost) throw new DemoReadOnlyError();
    endpoint = restoreEndpoint(endpoint);
    if (isReadPost && typeof fetchOptions.body === 'string') {
      fetchOptions.body = restoreBody(fetchOptions.body);
    }
  }

  const url = `${baseUrl}${endpoint}`;

  // Acquire bearer token for the API
  const token = await getApiToken();
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const config = {
    // Bypass the browser HTTP cache. Without this, fetch's default cache mode
    // can serve a stale response to user-initiated Refresh clicks because the
    // API responses don't send Cache-Control headers that forbid it.
    cache: 'no-store',
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...optionHeaders,
    },
  };

  if (!config.body && (config.method === 'GET' || !config.method)) {
    delete config.headers['Content-Type'];
  }

  let response;
  try {
    response = await fetch(url, config);
  } catch (netErr) {
    // Network-level failure ("Failed to fetch") — no HTTP status, so it bypasses the
    // 401 auto-relogin below. For idempotent GETs, try ONE silent token refresh + retry
    // (recovers the common stale-token-between-load-and-fetch case). If it still fails,
    // surface an actionable message and raise the global connection-error banner.
    // Non-GET is not retried (avoids double-submitting a write that may have landed).
    const method = (config.method || 'GET').toUpperCase();
    if (method === 'GET') {
      try {
        const fresh = await getApiToken();
        if (fresh) config.headers = { ...config.headers, Authorization: `Bearer ${fresh}` };
        response = await fetch(url, config);
      } catch {
        notifyConnectionError();
        const err = new Error(CONNECTION_ERROR_MSG);
        err.isConnectionError = true;
        throw err;
      }
    } else {
      notifyConnectionError();
      const err = new Error(CONNECTION_ERROR_MSG);
      err.isConnectionError = true;
      throw err;
    }
  }

  if (response.status === 401) {
    console.warn('[API] Received 401 — session expired, redirecting to login...');
    await forceRelogin();
    throw new Error('Session expired. Please sign in again.');
  }

  if (!response.ok) {
    let errorMessage;
    let parsedBody = null;
    try {
      const errorData = await response.json();
      parsedBody = errorData;
      errorMessage = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
      if (errorData.details) {
        errorMessage += ` (${errorData.details})`;
      }
      if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        const listed = formatErrorList(errorData.errors);
        if (listed) errorMessage += ': ' + listed;
      }
    } catch {
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    const err = new Error(DEMO ? scrubFreeText(errorMessage) : errorMessage);
    err.status = response.status;
    // Carry the parsed JSON body so callers can read structured fields
    // (e.g. 409 conflicts that return lastUpdated for confirmation prompts).
    // Deep-anonymize it in Demo Mode: callers render fields off err.body
    // (CaseDetail overlays e.body.enrichment from the 409 freshness response).
    if (parsedBody) err.body = DEMO ? anonymizeDeep(parsedBody) : parsedBody;
    throw err;
  }

  if (response.status === 204) {
    return null;
  }

  // Success bodies are JSON unless the caller asked for text. The Executive Report
  // content endpoint returns text/html, and an unconditional response.json() here
  // would throw before the caller ever saw it. Routing it through the wrapper rather
  // than a one-off raw fetch keeps token acquisition, the 401 auto-relogin, the
  // network-retry path, structured error bodies, cache:'no-store' and demo-mode
  // handling — all of which a bare fetch would silently lose.
  // ERROR responses stay JSON-parsed above regardless of the requested success type.
  if (_responseType === 'text') {
    const text = await response.text();
    const scrubbed = DEMO ? scrubFreeText(text) : text;
    return _returnResponse ? { body: scrubbed, response } : scrubbed;
  }

  const parsed = await response.json();
  const body = DEMO ? anonymizeApiResponse(parsed) : parsed;

  // When returnResponse is set, return both body and response (for ETag extraction)
  if (_returnResponse) {
    return { body, response };
  }

  return body;
}

/**
 * Pass-through shim retained from the pre-v1.57.0 mock-fallback wrapper. The
 * mock branch was removed; callers using this wrapper now just call the real
 * API. The second argument is ignored for back-compat with existing call sites.
 */
async function withMockFallback(apiFn, _mockFnIgnored) {
  return apiFn();
}

/**
 * Mock data was removed in v1.57.0 — this always returns false. Retained as
 * a stable export so any caller checking the flag keeps compiling.
 * @returns {boolean}
 */
export function isUsingMockData() {
  return false;
}

// ---------------------------------------------------------------------------
// Tenant Endpoints
// ---------------------------------------------------------------------------

export async function fetchTenants() {
  return withMockFallback(
    () => request('/api/tenants'),
    () => mockApi.fetchTenants()
  );
}

// ---------------------------------------------------------------------------
// Case Endpoints
// ---------------------------------------------------------------------------

export async function createCase(data) {
  return withMockFallback(
    async () => {
      const created = await request('/api/cases', { method: 'POST', body: JSON.stringify(data) });
      return normalizeCase(created);
    },
    async () => {
      const created = await mockApi.createCase(data);
      return normalizeCase(created);
    }
  );
}

export async function fetchCases(statusFilter) {
  return withMockFallback(
    async () => {
      const params = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const cases = await request(`/api/cases${params}`);
      return (Array.isArray(cases) ? cases : []).map(normalizeCase);
    },
    async () => {
      const cases = await mockApi.fetchCases(statusFilter);
      return cases.map(normalizeCase);
    }
  );
}

export async function fetchCase(caseId) {
  return withMockFallback(
    async () => {
      const data = await request(`/api/cases/${encodeURIComponent(caseId)}`);
      return normalizeCase(data);
    },
    async () => {
      const data = await mockApi.fetchCase(caseId);
      return normalizeCase(data);
    }
  );
}

export async function updateCase(caseId, data) {
  return withMockFallback(
    async () => {
      const updated = await request(`/api/cases/${encodeURIComponent(caseId)}`, { method: 'PUT', body: JSON.stringify(data) });
      return normalizeCase(updated);
    },
    async () => {
      const updated = await mockApi.updateCase(caseId, data);
      return normalizeCase(updated);
    }
  );
}

export async function deleteCase(caseId) {
  return request(`/api/cases/${encodeURIComponent(caseId)}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Executive Report (AI) Endpoints
//
// Generation is asynchronous by necessity: it takes 1-4 minutes and Azure's HTTP
// gateway hard-limits a request at ~230s. requestExecutiveReport returns 202 with
// a reportId; the caller polls fetchExecutiveReportStatus until Completed, then
// fetches the content.
// ---------------------------------------------------------------------------

// `reportType` selects the document: 'executive-report' (the multi-page report) or
// 'executive-brief' (the one-pager). OMITTED means the report -- the server's registry
// resolves an absent value to that, which is what every caller sent before the Brief
// existed. The vocabulary is the server's; REPORT_TYPES in ExecutiveReportModal.jsx is the
// only place it is mirrored client-side.
export async function requestExecutiveReport(caseId, factPack, reportType) {
  return request(`/api/cases/${encodeURIComponent(caseId)}/executive-reports`, {
    method: 'POST',
    body: JSON.stringify(reportType ? { factPack, reportType } : { factPack }),
  });
}

export async function fetchExecutiveReports(caseId) {
  return request(`/api/cases/${encodeURIComponent(caseId)}/executive-reports`);
}

export async function fetchExecutiveReportStatus(caseId, reportId) {
  return request(`/api/cases/${encodeURIComponent(caseId)}/executive-reports/${encodeURIComponent(reportId)}`);
}

export async function fetchExecutiveReportContent(caseId, reportId) {
  // text/html, not JSON — see the _responseType branch in request().
  return request(
    `/api/cases/${encodeURIComponent(caseId)}/executive-reports/${encodeURIComponent(reportId)}/content`,
    { _responseType: 'text' },
  );
}

// ---------------------------------------------------------------------------
// Runbook Execution Endpoints
// ---------------------------------------------------------------------------

export async function triggerRunbook(caseId, params = {}) {
  return withMockFallback(
    () => request(`/api/cases/${encodeURIComponent(caseId)}/run`, { method: 'POST', body: JSON.stringify(params) }),
    () => mockApi.triggerRunbook(caseId)
  );
}

export async function pollRunbookStatus(caseId, jobId) {
  return withMockFallback(
    () => request(`/api/cases/${encodeURIComponent(caseId)}/run/${encodeURIComponent(jobId)}/status`),
    () => mockApi.pollRunbookStatus(caseId, jobId)
  );
}

export async function fetchCaseResults(caseId) {
  return withMockFallback(
    async () => {
      const data = await request(`/api/cases/${encodeURIComponent(caseId)}/results`);
      return normalizeResults(data);
    },
    () => mockApi.fetchCaseResults(caseId)
  );
}

// ---------------------------------------------------------------------------
// Service IP Endpoints
// ---------------------------------------------------------------------------

export async function fetchServiceIPs() {
  return withMockFallback(
    () => request('/api/service-ips'),
    () => mockApi.fetchServiceIPs()
  );
}

export async function addServiceIP(data) {
  return withMockFallback(
    () => request('/api/service-ips', { method: 'POST', body: JSON.stringify(data) }),
    () => mockApi.addServiceIP(data)
  );
}

export async function removeServiceIP(ipAddress) {
  return withMockFallback(
    () => request(`/api/service-ips/${encodeURIComponent(ipAddress)}`, { method: 'DELETE' }),
    () => mockApi.removeServiceIP(ipAddress)
  );
}

// ---------------------------------------------------------------------------
// Known Enterprise App Endpoints
// ---------------------------------------------------------------------------

export async function fetchKnownApps(tenantId) {
  return withMockFallback(
    () => {
      const params = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
      return request(`/api/known-apps${params}`);
    },
    () => mockApi.fetchKnownApps(tenantId)
  );
}

export async function addKnownApp(data) {
  return withMockFallback(
    () => request('/api/known-apps', { method: 'POST', body: JSON.stringify(data) }),
    () => mockApi.addKnownApp(data)
  );
}

export async function removeKnownApp(appId, tenantId = 'GLOBAL') {
  return withMockFallback(
    () => request(`/api/known-apps/${encodeURIComponent(appId)}?tenantId=${encodeURIComponent(tenantId)}`, { method: 'DELETE' }),
    () => mockApi.removeKnownApp(appId, tenantId)
  );
}

// ---------------------------------------------------------------------------
// Tenant Safe IP Endpoints
// ---------------------------------------------------------------------------

export async function fetchTenantSafeIPs(tenantId) {
  return withMockFallback(
    () => request(`/api/tenants/${encodeURIComponent(tenantId)}/safe-ips`),
    () => Promise.resolve([])
  );
}

export async function addTenantSafeIP(tenantId, data) {
  return withMockFallback(
    () => request(`/api/manage/tenants/${encodeURIComponent(tenantId)}/safe-ips`, { method: 'POST', body: JSON.stringify(data) }),
    () => Promise.resolve({ ipAddress: data.ipAddress, label: data.label || '', addedBy: 'Mock', addedDate: new Date().toISOString() })
  );
}

export async function removeTenantSafeIP(tenantId, ipAddress) {
  return withMockFallback(
    () => request(`/api/manage/tenants/${encodeURIComponent(tenantId)}/safe-ips/${encodeURIComponent(ipAddress)}`, { method: 'DELETE' }),
    () => Promise.resolve(null)
  );
}

// ---------------------------------------------------------------------------
// MSP Notification Settings (per-MSP alert email routing — admin only)
// ---------------------------------------------------------------------------

export async function fetchMspNotificationSettings() {
  return withMockFallback(
    () => request('/api/msp-notifications'),
    () => Promise.resolve([])
  );
}

export async function updateMspNotificationSetting(mspCode, data) {
  return withMockFallback(
    () => request(`/api/manage/msp-notifications/${encodeURIComponent(mspCode)}`, { method: 'PUT', body: JSON.stringify(data) }),
    () => Promise.resolve({ mspCode, emailRecipients: data.emailRecipients || '', emailEnabled: !!data.emailEnabled, updatedBy: 'Mock', updatedUtc: new Date().toISOString() })
  );
}

// ---------------------------------------------------------------------------
// Ticketing Config (per-MSP PSA ticketing routing — admin only)
// Credentials are write-only: the API never returns secret values.
// ---------------------------------------------------------------------------

export async function fetchTicketingConfigs() {
  return withMockFallback(
    () => request('/api/ticketing-config'),
    () => Promise.resolve([])
  );
}

export async function updateTicketingConfig(mspCode, data) {
  return withMockFallback(
    () => request(`/api/manage/ticketing-config/${encodeURIComponent(mspCode)}`, { method: 'PUT', body: JSON.stringify(data) }),
    () => Promise.resolve({ mspCode, ticketingSystem: data.ticketingSystem || 'disabled', enabled: !!data.enabled, kvSecretName: `ticketing-${mspCode}`, credentialsConfigured: !!data.bundle, updatedBy: 'Mock', updatedUtc: new Date().toISOString() })
  );
}

export async function testTicketingConnection(mspCode, data) {
  return withMockFallback(
    () => request(`/api/manage/ticketing-config/${encodeURIComponent(mspCode)}/test`, { method: 'POST', body: JSON.stringify(data) }),
    () => Promise.resolve({ ok: true, message: 'Mock: connection OK' })
  );
}

// Exercise the ticket write-path against the PSA (create/update/close a test
// ticket). data = { action: 'create'|'update'|'close', ticketId?, ticketingSystem?,
// bundle?|useSaved?, tenantId? } — tenantId pulls that tenant's routing row.
export async function ticketingTestTicket(mspCode, data) {
  return withMockFallback(
    () => request(`/api/manage/ticketing-config/${encodeURIComponent(mspCode)}/test-ticket`, { method: 'POST', body: JSON.stringify(data) }),
    () => Promise.resolve({ ok: true, ticketId: 'TEST-123', ticketNumber: 'TEST-123', message: `Mock: ${data?.action || 'action'} ok` })
  );
}

// Fetch live { id, name } option lists from the MSP's PSA for routing dropdowns.
// type = companies | boards | statuses | priorities; boardId required for statuses.
export async function fetchTicketingOptions(mspCode, type, boardId) {
  const qs = new URLSearchParams({ type });
  if (boardId) qs.set('boardId', `${boardId}`);
  return withMockFallback(
    () => request(`/api/manage/ticketing-config/${encodeURIComponent(mspCode)}/options?${qs.toString()}`),
    () => Promise.resolve({ system: 'connectwise', type, options: [] })
  );
}

// ---------------------------------------------------------------------------
// MSP Webhook Config (per-MSP webhook destination — MSP-admin scoped)
// ---------------------------------------------------------------------------
export async function fetchMspWebhookConfigs() {
  return withMockFallback(
    () => request('/api/msp-webhooks'),
    () => Promise.resolve([])
  );
}
export async function updateMspWebhookConfig(mspCode, data) {
  return withMockFallback(
    () => request(`/api/manage/msp-webhooks/${encodeURIComponent(mspCode)}`, { method: 'PUT', body: JSON.stringify(data) }),
    () => Promise.resolve({ mspCode, webhookUrl: data.webhookUrl || '', enabled: !!data.enabled, updatedBy: 'Mock', updatedUtc: new Date().toISOString() })
  );
}
export async function removeMspWebhookConfig(mspCode) {
  return withMockFallback(
    () => request(`/api/manage/msp-webhooks/${encodeURIComponent(mspCode)}`, { method: 'DELETE' }),
    () => Promise.resolve({ mspCode, removed: true })
  );
}

// ---------------------------------------------------------------------------
// Per-tenant PSA ticketing routing (MSP-admin scoped)
// ---------------------------------------------------------------------------
export async function fetchTenantTicketingRoutes() {
  return withMockFallback(
    () => request('/api/tenant-ticketing-routes'),
    () => Promise.resolve([])
  );
}
export async function updateTenantTicketingRoute(tenantId, data) {
  return withMockFallback(
    () => request(`/api/manage/tenant-ticketing-routes/${encodeURIComponent(tenantId)}`, { method: 'PUT', body: JSON.stringify(data) }),
    () => Promise.resolve({ tenantId, ...data, configured: true, updatedBy: 'Mock', updatedUtc: new Date().toISOString() })
  );
}
export async function removeTenantTicketingRoute(tenantId) {
  return withMockFallback(
    () => request(`/api/manage/tenant-ticketing-routes/${encodeURIComponent(tenantId)}`, { method: 'DELETE' }),
    () => Promise.resolve({ tenantId, removed: true })
  );
}

// ---------------------------------------------------------------------------
// AiTM Trust Override Endpoints (read-open, write-admin)
// ---------------------------------------------------------------------------

export async function fetchAitmTrustedDomains() {
  return withMockFallback(
    () => request('/api/aitm-trusted-domains'),
    () => Promise.resolve([])
  );
}

export async function addAitmTrustedDomain(data) {
  return withMockFallback(
    () => request('/api/manage/aitm-trusted-domains', { method: 'POST', body: JSON.stringify(data) }),
    () => Promise.resolve({ domain: data.domain, notes: data.notes || '', addedBy: 'Mock', addedUtc: new Date().toISOString() })
  );
}

export async function updateAitmTrustedDomain(domain, data) {
  return withMockFallback(
    () => request(`/api/manage/aitm-trusted-domains/${encodeURIComponent(domain)}`, { method: 'PUT', body: JSON.stringify(data) }),
    () => Promise.resolve({ domain, notes: data.notes || '', addedBy: 'Mock', addedUtc: new Date().toISOString() })
  );
}

export async function removeAitmTrustedDomain(domain) {
  return withMockFallback(
    () => request(`/api/manage/aitm-trusted-domains/${encodeURIComponent(domain)}`, { method: 'DELETE' }),
    () => Promise.resolve(null)
  );
}

export async function fetchAitmTrustedPairs(tenantId) {
  const qs = tenantId && tenantId !== 'all' ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  return withMockFallback(
    () => request(`/api/aitm-trusted-pairs${qs}`),
    () => Promise.resolve([])
  );
}

export async function addAitmTrustedPair(data) {
  return withMockFallback(
    () => request('/api/manage/aitm-trusted-pairs', { method: 'POST', body: JSON.stringify(data) }),
    () => Promise.resolve({ ...data, addedBy: 'Mock', addedUtc: new Date().toISOString() })
  );
}

export async function updateAitmTrustedPair(tenantId, rowKey, data) {
  return withMockFallback(
    () => request(`/api/manage/aitm-trusted-pairs/${encodeURIComponent(tenantId)}/${encodeURIComponent(rowKey)}`, { method: 'PUT', body: JSON.stringify(data) }),
    () => Promise.resolve({ tenantId, rowKey, ...data, addedBy: 'Mock', addedUtc: new Date().toISOString() })
  );
}

export async function removeAitmTrustedPair(tenantId, rowKey) {
  return withMockFallback(
    () => request(`/api/manage/aitm-trusted-pairs/${encodeURIComponent(tenantId)}/${encodeURIComponent(rowKey)}`, { method: 'DELETE' }),
    () => Promise.resolve(null)
  );
}

// ---------------------------------------------------------------------------
// Rule-scoped Mailbox-Pair Allowlist (v1.60.0)
// Drives rule-008 (`rule_anomalous_mailbox_access`) suppression. Read-open,
// admin-only writes. PartitionKey = `{ruleId}|{tenantId}` (or `{ruleId}|*`),
// RowKey = sha1(target|actor_type|actor_value)[:32] (server-computed).
// ---------------------------------------------------------------------------

export async function fetchMailboxPairAllowlist(ruleId, tenantId) {
  const params = new URLSearchParams();
  if (ruleId)   params.set('ruleId', ruleId);
  if (tenantId) params.set('tenantId', tenantId);
  const qs = params.toString();
  return request(`/api/mailbox-pair-allowlist${qs ? `?${qs}` : ''}`);
}

export async function fetchMailboxPairAllowlistSentinelHealth() {
  return request('/api/mailbox-pair-allowlist/sentinel-health');
}

export async function addMailboxPairAllowlistEntry(data) {
  return request('/api/manage/mailbox-pair-allowlist', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateMailboxPairAllowlistEntry(partitionKey, rowKey, data) {
  return request(
    `/api/manage/mailbox-pair-allowlist/${encodeURIComponent(partitionKey)}/${encodeURIComponent(rowKey)}`,
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function removeMailboxPairAllowlistEntry(partitionKey, rowKey) {
  return request(
    `/api/manage/mailbox-pair-allowlist/${encodeURIComponent(partitionKey)}/${encodeURIComponent(rowKey)}`,
    { method: 'DELETE' }
  );
}

export async function reconcileMailboxPairAllowlistSentinel(ruleId) {
  return request(
    `/api/manage/mailbox-pair-allowlist/${encodeURIComponent(ruleId)}/reconcile-sentinel`,
    { method: 'POST' }
  );
}

// ---------------------------------------------------------------------------
// Teams-Phone Device-Code Suppression (v1.71.0)
// Two pipeline-owned tables the portal writes to:
//   RuleScopedAccountAllowlist — Condition A (per rule+tenant appliance accounts);
//     PartitionKey = `{ruleId}|{tenantId}`, RowKey = account UPN (lowercased).
//   TeamsPhoneDeviceModels — Condition B (global device-name patterns);
//     PartitionKey = 'global', RowKey = pattern slug (server-derived).
// Read-open, admin-only writes. Every write bumps a __version sentinel the
// pipeline polls. Soft-delete (enabled=false) — never hard-deletes.
// ---------------------------------------------------------------------------

export async function fetchAccountAllowlist(ruleId, tenantId) {
  const params = new URLSearchParams();
  if (ruleId)   params.set('ruleId', ruleId);
  if (tenantId) params.set('tenantId', tenantId);
  const qs = params.toString();
  return request(`/api/account-allowlist${qs ? `?${qs}` : ''}`);
}

export async function fetchAccountAllowlistSentinelHealth() {
  return request('/api/account-allowlist/sentinel-health');
}

export async function addAccountAllowlistEntry(data) {
  return request('/api/manage/account-allowlist', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAccountAllowlistEntry(partitionKey, rowKey, data) {
  return request(
    `/api/manage/account-allowlist/${encodeURIComponent(partitionKey)}/${encodeURIComponent(rowKey)}`,
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function removeAccountAllowlistEntry(partitionKey, rowKey) {
  return request(
    `/api/manage/account-allowlist/${encodeURIComponent(partitionKey)}/${encodeURIComponent(rowKey)}`,
    { method: 'DELETE' }
  );
}

export async function reconcileAccountAllowlistSentinel(ruleId) {
  return request(
    `/api/manage/account-allowlist/${encodeURIComponent(ruleId)}/reconcile-sentinel`,
    { method: 'POST' }
  );
}

// ---------------------------------------------------------------------------
// Rule-Scoped (User, Application) Pair Allowlist (skip_if_in_user_app_pair_allowlist,
// v1.83.0). Per-(rule, tenant) (user, app GUID) pairs whose matches are suppressed.
// The pipeline matches on the application GUID; the display name is informational.
// Same toggle-driven gate + version-sentinel + soft-delete pattern as the account
// allowlist. '*' wildcards on either side (not both).
// ---------------------------------------------------------------------------

export async function fetchUserAppPairAllowlist(ruleId, tenantId) {
  const params = new URLSearchParams();
  if (ruleId)   params.set('ruleId', ruleId);
  if (tenantId) params.set('tenantId', tenantId);
  const qs = params.toString();
  return request(`/api/user-app-pair-allowlist${qs ? `?${qs}` : ''}`);
}

export async function fetchUserAppPairAllowlistSentinelHealth() {
  return request('/api/user-app-pair-allowlist/sentinel-health');
}

export async function addUserAppPairAllowlistEntry(data) {
  return request('/api/manage/user-app-pair-allowlist', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateUserAppPairAllowlistEntry(partitionKey, rowKey, data) {
  return request(
    `/api/manage/user-app-pair-allowlist/${encodeURIComponent(partitionKey)}/${encodeURIComponent(rowKey)}`,
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function removeUserAppPairAllowlistEntry(partitionKey, rowKey) {
  return request(
    `/api/manage/user-app-pair-allowlist/${encodeURIComponent(partitionKey)}/${encodeURIComponent(rowKey)}`,
    { method: 'DELETE' }
  );
}

export async function reconcileUserAppPairAllowlistSentinel(ruleId) {
  return request(
    `/api/manage/user-app-pair-allowlist/${encodeURIComponent(ruleId)}/reconcile-sentinel`,
    { method: 'POST' }
  );
}

// ---------------------------------------------------------------------------
// Rule-Scoped (Group, Application) Pair Allowlist (skip_if_in_group_app_allowlist, v1.85.0).
// Group is matched by verbatim Entra display name; app by GUID with '*' wildcard.
// ---------------------------------------------------------------------------

export async function fetchGroupAppAllowlist(ruleId, tenantId) {
  const params = new URLSearchParams();
  if (ruleId)   params.set('ruleId', ruleId);
  if (tenantId) params.set('tenantId', tenantId);
  const qs = params.toString();
  return request(`/api/group-app-allowlist${qs ? `?${qs}` : ''}`);
}

export async function fetchGroupAppAllowlistSentinelHealth() {
  return request('/api/group-app-allowlist/sentinel-health');
}

export async function addGroupAppAllowlistEntry(data) {
  return request('/api/manage/group-app-allowlist', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateGroupAppAllowlistEntry(partitionKey, rowKey, data) {
  return request(
    `/api/manage/group-app-allowlist/${encodeURIComponent(partitionKey)}/${encodeURIComponent(rowKey)}`,
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function removeGroupAppAllowlistEntry(partitionKey, rowKey) {
  return request(
    `/api/manage/group-app-allowlist/${encodeURIComponent(partitionKey)}/${encodeURIComponent(rowKey)}`,
    { method: 'DELETE' }
  );
}

export async function reconcileGroupAppAllowlistSentinel(ruleId) {
  return request(
    `/api/manage/group-app-allowlist/${encodeURIComponent(ruleId)}/reconcile-sentinel`,
    { method: 'POST' }
  );
}

export async function fetchTeamsPhoneModels() {
  return request('/api/teams-phone-models');
}

export async function fetchTeamsPhoneModelsSentinelHealth() {
  return request('/api/teams-phone-models/sentinel-health');
}

export async function addTeamsPhoneModel(data) {
  return request('/api/manage/teams-phone-models', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTeamsPhoneModel(rowKey, data) {
  return request(`/api/manage/teams-phone-models/${encodeURIComponent(rowKey)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function removeTeamsPhoneModel(rowKey) {
  return request(`/api/manage/teams-phone-models/${encodeURIComponent(rowKey)}`, {
    method: 'DELETE',
  });
}

export async function reconcileTeamsPhoneModelsSentinel() {
  return request('/api/manage/teams-phone-models/reconcile-sentinel', { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Defender File-Hash Allowlist (read-open, write-admin; cross-tenant). Identity
// is composite (hash = PartitionKey, threat = RowKey). PUT/DELETE pass the hash
// in the route and the threat in the ?threat= query (avoids '*' in a path segment).
// ---------------------------------------------------------------------------
export async function fetchDefenderFileHashAllowlist() {
  return request('/api/defender-file-hash-allowlist');
}

export async function fetchDefenderFileHashAllowlistSentinelHealth() {
  return request('/api/defender-file-hash-allowlist/sentinel-health');
}

export async function addDefenderFileHashEntry(data) {
  return request('/api/manage/defender-file-hash-allowlist', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDefenderFileHashEntry(hash, threat, data) {
  return request(`/api/manage/defender-file-hash-allowlist/${encodeURIComponent(hash)}?threat=${encodeURIComponent(threat)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function removeDefenderFileHashEntry(hash, threat) {
  return request(`/api/manage/defender-file-hash-allowlist/${encodeURIComponent(hash)}?threat=${encodeURIComponent(threat)}`, {
    method: 'DELETE',
  });
}

export async function reconcileDefenderFileHashAllowlistSentinel() {
  return request('/api/manage/defender-file-hash-allowlist/reconcile-sentinel', { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Defender Pass-through Benign-IP Allowlist (read-open, write=admin; global
// cross-tenant). Backs the skip_if_in_defender_passthrough_ip_allowlist tier:
// trusts an IP for the Microsoft-security pass-through rules even though Microsoft
// flagged it. Identity is the canonical IP (PartitionKey='global', RowKey=IP).
// PUT/DELETE pass the canonical IP in the route (encodeURIComponent; ':' in IPv6
// survives as one segment). Pipeline-owned table — portal does CRUD + sentinel only.
// ---------------------------------------------------------------------------
export async function fetchDefenderPassthroughIpAllowlist() {
  return request('/api/defender-passthrough-ip-allowlist');
}

export async function addDefenderPassthroughIpEntry(data) {
  return request('/api/manage/defender-passthrough-ip-allowlist', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDefenderPassthroughIpEntry(ip, data) {
  return request(`/api/manage/defender-passthrough-ip-allowlist/${encodeURIComponent(ip)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function removeDefenderPassthroughIpEntry(ip) {
  return request(`/api/manage/defender-passthrough-ip-allowlist/${encodeURIComponent(ip)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Spray-Origin IP Watchlist (read-open, revoke=admin; cross-tenant, pipeline-
// written). The portal reads rows + owns only the `enabled` flag + revoke audit
// fields. rowKey (ip|{ver}|{prefix_len}|{network}) rides in the body, not the
// route — it contains '|' and ':'. GET returns { entries, sentinel }.
// ---------------------------------------------------------------------------
export async function fetchSprayOriginWatchlist() {
  return request('/api/spray-origin-watchlist');
}

export async function fetchSprayOriginWatchlistSentinelHealth() {
  return request('/api/spray-origin-watchlist/sentinel-health');
}

export async function updateSprayOriginWatchlistEntry(data) {
  // data: { rowKey, enabled, disableReason?, prefixCidr? }
  return request('/api/manage/spray-origin-watchlist', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function fetchSprayOriginMatchingAlerts(prefix, tenantIds) {
  const tids = Array.isArray(tenantIds) ? tenantIds.join(',') : (tenantIds || '');
  return request(`/api/spray-origin-watchlist/matching-alerts?prefix=${encodeURIComponent(prefix)}&tenantIds=${encodeURIComponent(tids)}`);
}

export async function reconcileSprayOriginWatchlistSentinel() {
  return request('/api/manage/spray-origin-watchlist/reconcile-sentinel', { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Known Malicious IPs (read-open, write-admin; cross-tenant threat-intel)
// ---------------------------------------------------------------------------

export async function fetchKnownMaliciousIPs() {
  return withMockFallback(
    () => request('/api/known-malicious-ips'),
    () => Promise.resolve([])
  );
}

export async function addKnownMaliciousIP(data) {
  return withMockFallback(
    () => request('/api/manage/known-malicious-ips', { method: 'POST', body: JSON.stringify(data) }),
    () => Promise.resolve({
      ipAddress: data.ipAddress,
      type:      data.type,
      source:    data.source,
      notes:     data.notes || '',
      addedUtc:  new Date().toISOString(),
    })
  );
}

export async function updateKnownMaliciousIP(ipAddress, data) {
  return withMockFallback(
    () => request(`/api/manage/known-malicious-ips/${encodeURIComponent(ipAddress)}`, { method: 'PUT', body: JSON.stringify(data) }),
    () => Promise.resolve({
      ipAddress,
      type:     data.type ?? '',
      source:   data.source ?? '',
      notes:    data.notes ?? '',
      addedUtc: new Date().toISOString(),
    })
  );
}

export async function removeKnownMaliciousIP(ipAddress) {
  return withMockFallback(
    () => request(`/api/manage/known-malicious-ips/${encodeURIComponent(ipAddress)}`, { method: 'DELETE' }),
    () => Promise.resolve(null)
  );
}

// Bulk-add the IPs an analyst marked malicious in a case to the cross-tenant
// blocklist. Overwrites type='attacker' / notes='per case {caseId}' / source and
// refreshes the added date on existing rows. Global-admin only (server-enforced).
export async function addKnownMaliciousIPsFromCase(caseId, ips) {
  return withMockFallback(
    () => request('/api/manage/known-malicious-ips/bulk', {
      method: 'POST',
      body: JSON.stringify({ caseId, ips }),
    }),
    () => Promise.resolve({ caseId, added: (ips || []).length, updated: 0, failed: 0, results: [] })
  );
}

// ---------------------------------------------------------------------------
// Cross-Tenant IP Activity Search (Admin) — Known Malicious IPs page
// ---------------------------------------------------------------------------

export async function triggerIpActivitySearch(ipAddress, params = {}) {
  return request(`/api/manage/known-malicious-ips/${encodeURIComponent(ipAddress)}/search`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function getIpActivitySearchStatus(jobId) {
  return request(`/api/manage/known-malicious-ips/search/${encodeURIComponent(jobId)}/status`);
}

export async function getIpActivitySearchResults(jobId) {
  return request(`/api/manage/known-malicious-ips/search/${encodeURIComponent(jobId)}/results`);
}

// ---------------------------------------------------------------------------
// Coverage Analysis
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// BPC Tenants Management Endpoints (admin-only)
// ---------------------------------------------------------------------------

export async function fetchBpcTenants() {
  return request('/api/manage/bpc-tenants');
}

export async function addBpcTenant(data) {
  return request('/api/manage/bpc-tenants', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateBpcTenant(bpcTenantId, data) {
  return request(`/api/manage/bpc-tenants/${encodeURIComponent(bpcTenantId)}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function removeBpcTenant(bpcTenantId) {
  return request(`/api/manage/bpc-tenants/${encodeURIComponent(bpcTenantId)}`, { method: 'DELETE' });
}

export async function fetchDeviceInventory(tenantId, { lastSeenDays, workload, snapshotDate, bpcTenantId } = {}) {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenantId', tenantId);
  if (lastSeenDays != null) params.set('lastSeenDays', String(lastSeenDays));
  if (workload) params.set('workload', workload);
  if (snapshotDate) params.set('snapshotDate', snapshotDate);
  if (bpcTenantId) params.set('bpcTenantId', bpcTenantId);
  const qs = params.toString();
  return request(`/api/coverage/devices${qs ? `?${qs}` : ''}`);
}

// Entra identity inventory — daily snapshot of users / groups / directory roles /
// role members (read-only ATS mirror). collection ∈ 'users'|'groups'|'roles'|'role-members'.
export async function fetchEntraIdentityInventory(tenantId, { snapshotDate, collection } = {}) {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenantId', tenantId);
  if (snapshotDate) params.set('snapshotDate', snapshotDate);
  if (collection) params.set('collection', collection);
  const qs = params.toString();
  return request(`/api/coverage/entra-identity${qs ? `?${qs}` : ''}`);
}

// Tenant's Entra service principals (= Enterprise Applications) daily snapshot — the
// app picker source for the (group, application) allowlist. `appId` is the match key
// (== the appId in graph_signin logs); `displayName` is verbatim. v1.85.1.
export async function fetchEntraServicePrincipals(tenantId, { snapshotDate } = {}) {
  return fetchEntraIdentityInventory(tenantId, { snapshotDate, collection: 'service-principals' });
}

// ---------------------------------------------------------------------------
// Admin: SLO / Pipeline-Health Dashboard endpoints (admin-only on backend)
// ---------------------------------------------------------------------------

function buildSloQuery({ tenantId, workload, fields, days } = {}) {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenantId', tenantId);
  if (workload) params.set('workload', workload);
  if (fields) params.set('fields', fields);
  if (days != null) params.set('days', String(days));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function fetchSloCollectionCurrent(filters = {}) {
  return request(`/api/manage/slo/collection/current${buildSloQuery(filters)}`);
}

export async function fetchSloCollectionTrend({ days = 14 } = {}) {
  return request(`/api/manage/slo/collection/trend${buildSloQuery({ days })}`);
}

export async function fetchSloSilverStagingCurrent(filters = {}) {
  return request(`/api/manage/slo/silver-staging/current${buildSloQuery(filters)}`);
}

export async function fetchSloSilverStagingTrend({ days = 30 } = {}) {
  return request(`/api/manage/slo/silver-staging/trend${buildSloQuery({ days })}`);
}

export async function fetchSloIngestCurrent(filters = {}) {
  return request(`/api/manage/slo/ingest/current${buildSloQuery(filters)}`);
}

export async function fetchSloIngestTrend({ days = 14 } = {}) {
  return request(`/api/manage/slo/ingest/trend${buildSloQuery({ days })}`);
}

export async function fetchSloSnapshotToday() {
  return request('/api/manage/slo/snapshot/today');
}

// Delta -> ATS event-mirror completeness (Pipeline Health "Delta -> Mirror" tab).
// Reads the pipeline-owned SilverM365MirrorCompletenessDaily via SloMirrorCompleteness.
// A daily completeness RATE, not a liveness signal - a UTC day does not settle until
// ~02:00Z, so it cannot honestly be sampled more often than daily.
export async function fetchSloMirrorCompleteness({ days = 14 } = {}) {
  return request(`/api/manage/slo/mirror-completeness${buildSloQuery({ days })}`);
}

// ---------------------------------------------------------------------------
// Suppressed-alert telemetry ("Investigations Avoided"). Admin / MSP-scoped.
// ---------------------------------------------------------------------------
function buildSuppressedQuery({ dateFrom, dateTo, tenantId, mspCode, ruleMode, valueTier, tier, top } = {}) {
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  if (tenantId) params.set('tenantId', tenantId);
  if (mspCode) params.set('mspCode', mspCode);
  if (ruleMode) params.set('ruleMode', ruleMode);
  if (valueTier) params.set('valueTier', valueTier);
  if (tier) params.set('tier', tier);
  if (top != null) params.set('top', String(top));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function fetchSuppressedAlertsSummary(filters = {}) {
  return request(`/api/manage/suppressed-alerts/summary${buildSuppressedQuery(filters)}`);
}

export async function fetchSuppressedAlertsEvents(filters = {}) {
  return request(`/api/manage/suppressed-alerts/events${buildSuppressedQuery(filters)}`);
}

// ---------------------------------------------------------------------------
// Per-tenant daily cost (Cost dashboard). Admin / MSP-scoped. Reads the
// Databricks-written ATS mirror SilverM365TenantCostDaily via GetTenantCostDaily.
// ALPHA / internal cost intelligence — figures do NOT represent client charges.
// ---------------------------------------------------------------------------
function buildCostQuery({ dateFrom, dateTo, tenantId } = {}) {
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  if (tenantId) params.set('tenantId', tenantId);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getTenantCostDaily(filters = {}) {
  return request(`/api/manage/tenant-cost/daily${buildCostQuery(filters)}`);
}

// Per-tenant log-type cost drill-down (D37). tenantId REQUIRED — the log-type mirror is
// per-tenant and covers only the event/log-driven components. Reads the ATS mirror
// SilverM365TenantLogTypeCostDaily via GetTenantLogTypeCostDaily.
export async function getTenantLogTypeCostDaily(filters = {}) {
  return request(`/api/manage/tenant-cost/logtype-daily${buildCostQuery(filters)}`);
}

// Global (admin-only) Azure tag-total cost reconciliation (D36). Range-only; no tenant.
export async function getCostReconciliation(filters = {}) {
  return request(`/api/manage/cost-reconciliation/daily${buildCostQuery(filters)}`);
}

// Tenant size/profile (Cost dashboard drill) — server-aggregated active-user / group
// counts + the Global Admin roster from the daily Entra identity snapshot. tenantId
// REQUIRED; snapshotDate optional (server falls back today → -1 → -2 and returns the
// effective date). Reads GetTenantProfile.
export async function getTenantProfile({ tenantId, snapshotDate } = {}) {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenantId', tenantId);
  if (snapshotDate) params.set('snapshotDate', snapshotDate);
  const qs = params.toString();
  return request(`/api/manage/tenant-profile${qs ? `?${qs}` : ''}`);
}

// ---------------------------------------------------------------------------
// Admin: Managed Tenant Endpoints
// ---------------------------------------------------------------------------

export async function resolveTenantDomain(domain) {
  return withMockFallback(
    () => request('/api/manage/tenants/resolve', { method: 'POST', body: JSON.stringify({ domain }) }),
    () => mockApi.resolveTenantDomain(domain)
  );
}

export async function fetchManagedTenants() {
  return withMockFallback(
    async () => {
      const tenants = await request('/api/manage/tenants');
      return Array.isArray(tenants) ? tenants : [];
    },
    () => mockApi.fetchManagedTenants()
  );
}

export async function onboardTenant(data) {
  return withMockFallback(
    () => request('/api/manage/tenants', { method: 'POST', body: JSON.stringify(data) }),
    () => mockApi.onboardTenant(data)
  );
}

export async function activateTenant(tenantId) {
  return withMockFallback(
    () => request(`/api/manage/tenants/${encodeURIComponent(tenantId)}/activate`, { method: 'POST' }),
    () => mockApi.activateTenant(tenantId)
  );
}

export async function updateTenant(tenantId, data) {
  return withMockFallback(
    () => request(`/api/manage/tenants/${encodeURIComponent(tenantId)}`, { method: 'PUT', body: JSON.stringify(data) }),
    () => mockApi.updateTenant(tenantId, data)
  );
}

// Inactive lifecycle (2026-09-29). Deactivate is NOT a toggle any more: the body names the state.
//   { inactiveReason: 'paused' | 'retained' }  — also used to change an inactive tenant's state or cancel a
//   scheduled deletion. Reactivation is activateTenant().
export async function deactivateTenant(tenantId, { inactiveReason } = {}) {
  return request(`/api/manage/tenants/${encodeURIComponent(tenantId)}/deactivate`, {
    method: 'POST',
    body: JSON.stringify({ inactiveReason }),
  });
}

// SCHEDULES a deletion (status inactive + pending_deletion + purgeDate); deletes nothing immediately.
// purgeLogs (required) also requests deletion of the tenant's log data by the data teams.
export async function deleteTenant(tenantId, { purgeDate, purgeLogs } = {}) {
  return request(`/api/manage/tenants/${encodeURIComponent(tenantId)}`, {
    method: 'DELETE',
    body: JSON.stringify({ purgeDate, purgeLogs }),
  });
}

// ---------------------------------------------------------------------------
// Detection Alerts Endpoints
// ---------------------------------------------------------------------------

export async function fetchAlerts(tenantId, filters = {}) {
  return withMockFallback(
    async () => {
      const params = new URLSearchParams({ tenantId: tenantId || 'all' });
      if (filters.status) params.set('status', filters.status);
      if (filters.severity) params.set('severity', filters.severity);
      if (filters.hours !== undefined) params.set('hours', String(filters.hours));
      // Single-alert fast path (deep link) — point-read one alert, no scan.
      if (filters.alertId) params.set('alertId', filters.alertId);
      const data = await request(`/api/alerts?${params.toString()}`);
      return Array.isArray(data) ? data : [];
    },
    () => mockApi.fetchAlerts(tenantId, filters)
  );
}

// The portal detection alerts about ONE user inside a case window, for the case's All
// Events timeline. `users` is the case identifier set (addresses + Entra object id);
// `from`/`to` are UTC ISO strings, `to` exclusive. Each row carries `caseMatchBasis`
// ('user' = the alert's user, 'named' = named in the alert's raw event).
// No mock fallback: a failure must surface as "could not load", never as "no alerts".
export async function fetchCaseUserAlerts(tenantId, { users, from, to }) {
  const params = new URLSearchParams({ tenantId, users: (users || []).join(','), from, to });
  const data = await request(`/api/alerts?${params.toString()}`);
  return Array.isArray(data) ? data : [];
}

export async function fetchAlertSourceEvents(blobPath, tenantId, alertId) {
  // blobPath is the sourceEventsBlobPath value from the alert row with the
  // leading "blob:" stripped. v1.57.0 (MSP separation Phase 2): tenantId +
  // alertId are required for parent-row verification on the server so the
  // endpoint can't be used as an arbitrary blob proxy. Older two-arg callers
  // still work but log a LEGACY-CALL warning server-side.
  const params = new URLSearchParams({ blobPath });
  if (tenantId) params.set('tenantId', tenantId);
  if (alertId)  params.set('alertId',  alertId);
  return request(`/api/alerts/source-events?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Recent Events (read-only ATS-backed quick-look — 14-day mirror of 7 workloads)
// ---------------------------------------------------------------------------

export async function fetchRecentEvents({ tenantId, logTypes, hours, search, top } = {}) {
  // Reads the 14-day ATS mirror via GET /api/recent-events. Returns an envelope
  // { events, truncatedWorkloads, perWorkloadCap, totalMs }. The `top` param is
  // the PER-WORKLOAD cap (default 500), so each of the seven SilverM365* tables
  // contributes at most `top` rows independently. Read-only — no side effects.
  const params = new URLSearchParams();
  if (tenantId && tenantId !== 'all') params.set('tenantId', tenantId);
  if (Array.isArray(logTypes) && logTypes.length > 0) {
    params.set('logTypes', logTypes.join(','));
  }
  if (hours !== undefined && hours !== null) params.set('hours', String(hours));
  if (search) params.set('search', search);
  if (top !== undefined && top !== null) params.set('top', String(top));
  const qs = params.toString();
  const data = await request(qs ? `/api/recent-events?${qs}` : '/api/recent-events');
  // The endpoint historically returned a raw array; current envelope shape adds
  // truncation metadata. Defensive unwrap covers both shapes.
  if (Array.isArray(data)) {
    return { events: data, truncatedWorkloads: [], perWorkloadCap: null, totalMs: null };
  }
  return {
    events: Array.isArray(data?.events) ? data.events : [],
    truncatedWorkloads: Array.isArray(data?.truncatedWorkloads) ? data.truncatedWorkloads : [],
    perWorkloadCap: typeof data?.perWorkloadCap === 'number' ? data.perWorkloadCap : null,
    totalMs: typeof data?.totalMs === 'number' ? data.totalMs : null,
  };
}

export async function fetchPortalActivity({ dateFrom, dateTo, category, actor, tenantId, search, top } = {}) {
  // Reads the centralized portal activity (audit) log via GET /api/manage/portal-activity.
  // Global-admin-only server-side. Returns an array of activity rows newest-first.
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo)   params.set('dateTo', dateTo);
  if (category) params.set('category', category);
  if (actor)    params.set('actor', actor);
  if (tenantId) params.set('tenantId', tenantId);
  if (search)   params.set('search', search);
  if (top !== undefined && top !== null) params.set('top', String(top));
  const qs = params.toString();
  const data = await request(qs ? `/api/manage/portal-activity?${qs}` : '/api/manage/portal-activity');
  if (!Array.isArray(data)) return [];
  // Defensive: PowerShell array-return semantics can occasionally double-wrap the
  // payload ([[row]] instead of [row]); flatten one level so the table renders.
  return data.flat().filter((r) => r && typeof r === 'object');
}

export async function logSubjectView({ alertId, caseId, tenantId, mailbox, subjectCount, ruleName, source, blobPath, messages, messageCount } = {}) {
  // Audit a PHI/PII view: records that the caller revealed masked email subject
  // lines on an alert/test-run row. Fire-and-forget + fail-soft — a logging error
  // must never block the analyst's reveal. Sends opaque message identifiers
  // (id/folder/size) + the evidence blob pointer to tie the record to the actual
  // messages, but NEVER the subject text itself.
  try {
    await request('/api/activity/subject-view', {
      method: 'POST',
      body: JSON.stringify({ alertId, caseId, tenantId, mailbox, subjectCount, ruleName, source, blobPath, messages, messageCount }),
    });
  } catch {
    /* swallow — see fail-soft note above */
  }
}

export async function fetchRecentEventSourceEvent(blobPath, { tenantId, logType, partitionKey, rowKey } = {}) {
  // Lazy fetch for ATS rows where raw_event_json spilled to the event-evidence
  // blob container. v1.57.0 (MSP separation Phase 2): pass tenantId + logType
  // + partitionKey + rowKey so the server can re-read the parent row and verify
  // the stored raw_event_blob_path matches the request before downloading.
  const params = new URLSearchParams({ blobPath });
  if (tenantId)     params.set('tenantId',     tenantId);
  if (logType)      params.set('logType',      logType);
  if (partitionKey) params.set('partitionKey', partitionKey);
  if (rowKey)       params.set('rowKey',       rowKey);
  return request(`/api/recent-events/source-event?${params.toString()}`);
}

export async function fetchDevicePosture(tenantId, deviceId) {
  // Resolves an alert's device against SilverM365EntraDevices + SilverM365IntuneDevices.
  // Returns { entra, intune, lookupTenantId, lookupDeviceId } — either side may be null.
  const params = new URLSearchParams();
  if (tenantId) params.set('tenantId', tenantId);
  if (deviceId) params.set('deviceId', deviceId);
  return request(`/api/alerts/device-posture?${params.toString()}`);
}

export async function updateAlertStatus(alertId, tenantId, status, closeReason) {
  const payload = { status };
  if (closeReason !== undefined && closeReason !== null) payload.closeReason = closeReason;
  return withMockFallback(
    () => request(
      `/api/alerts/${encodeURIComponent(alertId)}?tenantId=${encodeURIComponent(tenantId)}`,
      { method: 'PUT', body: JSON.stringify(payload) }
    ),
    () => mockApi.updateAlertStatus(alertId, tenantId, status, closeReason)
  );
}

// PSA ticket linkage for an alert (read-only). Looks up the dispatcher's ticket
// marker row in DetectionDispatchLog. Returns { ticketLinked, ticketId,
// ticketNumber, ticketingSystem, sendMode, ticketOpenedUtc } — ticketLinked is
// false for unlinked or stub-mode alerts.
export async function getAlertTicket(alertId, tenantId) {
  return withMockFallback(
    () => request(
      `/api/alerts/${encodeURIComponent(alertId)}/ticket?tenantId=${encodeURIComponent(tenantId)}`
    ),
    () => Promise.resolve({ ticketLinked: false })
  );
}

// Push the alert resolution to the linked PSA ticket: add the close reason as a
// note, optionally log a time entry (when timeMinutes > 0 AND the caller has a PSA
// user mapping for the MSP), then close the ticket. No-op success when no live
// ticket is linked. Returns { ticketLinked, ticketId, ticketingSystem, noteAdded,
// closed, timeLogged, message, timeMessage }.
export async function closeAlertTicket(alertId, tenantId, closeReason, timeMinutes = 0) {
  const payload = { tenantId, closeReason };
  if (timeMinutes && timeMinutes > 0) payload.timeMinutes = Math.round(timeMinutes);
  return withMockFallback(
    () => request(
      `/api/alerts/${encodeURIComponent(alertId)}/close-ticket`,
      { method: 'POST', body: JSON.stringify(payload) }
    ),
    () => Promise.resolve({ ticketLinked: false, closed: false, noteAdded: false, timeLogged: false, message: 'mock' })
  );
}

// ---------------------------------------------------------------------------
// Portal User -> PSA User mapping (admin: global + MSP-scoped)
// ---------------------------------------------------------------------------

// Returns { users, mappings, msps } scoped to the caller's admin visibility.
export async function fetchPortalUserMappings() {
  return withMockFallback(
    () => request('/api/portal-user-mappings'),
    () => Promise.resolve({ users: [], mappings: [], msps: [] })
  );
}

// Upsert (or, with an empty psaResourceId, delete) one portal-user -> PSA mapping.
export async function updatePortalUserMapping(mspCode, data) {
  return withMockFallback(
    () => request(
      `/api/manage/portal-user-mappings/${encodeURIComponent(mspCode)}`,
      { method: 'PUT', body: JSON.stringify(data) }
    ),
    () => Promise.resolve({ mspCode, upn: data.upn, mapped: !!data.psaResourceId })
  );
}

// Fetch the MSP's PSA users (AutoTask resources / ConnectWise members) for the
// mapping dropdown. Returns { system, resources: [{ id, name, email }] }.
export async function fetchPsaResources(mspCode) {
  return request(`/api/manage/ticketing-config/${encodeURIComponent(mspCode)}/resources`);
}

// Cross-tenant IP prevalence — single Databricks count query, drives the
// "shared-VPN vs isolated compromise" badge in the IP Intelligence panel.
// alertId in the route path is decorative; only tenantId + ip are required.
export async function getIpPrevalence(alertId, tenantId, ip) {
  const aId = alertId || 'lookup';
  const params = new URLSearchParams({ tenantId, ip });
  return request(
    `/api/alerts/${encodeURIComponent(aId)}/ip-prevalence?${params.toString()}`
  );
}

// Batch GUID -> friendly-name resolver for directory_audit alert display.
// Resolves group / user / directory-role GUIDs (e.g. a Conditional Access
// policy's includeGroups) against the daily Entra identity snapshot tables.
// Returns { tenantId, resolved: { "<idLower>": { name, type, upn? } } }.
export async function resolveDirectoryNames(tenantId, ids, snapshotDate) {
  if (!tenantId || !Array.isArray(ids) || ids.length === 0) return { resolved: {} };
  const params = new URLSearchParams({ tenantId, ids: ids.join(',') });
  if (snapshotDate) params.set('snapshotDate', snapshotDate);
  return request(`/api/alerts/resolve-directory-names?${params.toString()}`);
}

// Manual "Resolve Name" button for a single directory object GUID on an alert's
// Target Resource panel that the daily Entra snapshot doesn't have yet. Queries
// ATS (snapshot + 30-min directory_audit mirror) and persists the resolved name
// onto the alert row. Returns { found, id, name?, type?, reason? }.
export async function resolveDirectoryName(alertId, { tenantId, id, eventTimeUtc }) {
  const aId = alertId || 'lookup';
  return request(`/api/alerts/${encodeURIComponent(aId)}/resolve-directory-name`, {
    method: 'POST',
    body: JSON.stringify({ tenantId, id, eventTimeUtc }),
  });
}

// "Who else used this IP?" — on-demand companion to getIpPrevalence, over 30 days.
//
// 🔴 THREE GROUPS, not two. This comment used to say cross-tenant UPNs were withheld
// wholesale, which has been false since v1.110.3 and is what produced a TODO asking for
// an endpoint that already existed. The response carries:
//   users[]            — same-tenant UPNs, with per-user activity detail
//   crossTenantUsers[] — identities in OTHER tenants the caller IS AUTHORIZED to read,
//                        each labelled with its tenant (server-scoped from
//                        Get-UserMspContext), plus crossTenantTruncated at a 500 cap
//   withheldUserCount / withheldTenantCount — COUNTS ONLY, for tenants outside that scope
//
// The route's alertId is DECORATIVE (the endpoint never looks an alert up), so a case row
// passes its case id. Authorization is entirely the server's, which is why the control is
// open to every portal user. Rendered by components/ui/IpPrevalenceUsers.jsx — one
// component for the Alerts panel and the case IP Addresses tab.
// IP Search page (v1.196.0): every account that used `ip` between `from` and `to`
// (yyyy-MM-dd, UTC days), across the tenants the caller may read; counts only for the rest.
// Same response shape as getIpPrevalenceUsers, rendered by the same ui/IpPrevalenceUsers.
export async function searchIpAccounts(ip, from, to) {
  const params = new URLSearchParams({ ip, from, to });
  return request(`/api/ip-accounts?${params.toString()}`);
}

export async function getIpPrevalenceUsers(alertId, tenantId, ip) {
  const aId = alertId || 'lookup';
  const params = new URLSearchParams({ tenantId, ip });
  return request(
    `/api/alerts/${encodeURIComponent(aId)}/ip-prevalence/users?${params.toString()}`
  );
}

// User behavior baseline for the case Baseline tab. Reads the 7-day + 30-day
// snapshot for a given case (server resolves tenant/upn/IOC-anchor from the
// case row). snapshotDate optional — omit for the IOC-anchored default; pass a
// YYYY-MM-DD to load a specific snapshot. Returns { availableDates, sevenDay,
// thirtyDay, snapshotDate, iocDate, ... }.
export async function getUserBaseline(caseId, { snapshotDate } = {}) {
  const qs = snapshotDate ? `?snapshotDate=${encodeURIComponent(snapshotDate)}` : '';
  return request(`/api/cases/${encodeURIComponent(caseId)}/baseline${qs}`);
}

// Trace-session: synchronous; backend runs three Databricks SQL passes and
// returns 5-15s later. Admin-only on the backend. tenantId / sessionId /
// startUtc / userId all supplied via body — the endpoint does NOT look up
// the alert row in DetectionAlerts (so this works for test-result rows too,
// which the Alerts page renders via the Show Test Alerts toggle).
export async function postSessionTrace(alertId, { tenantId, sessionId, startUtc, userId, hours = 24 }) {
  const aId = alertId || 'lookup';
  return request(
    `/api/alerts/${encodeURIComponent(aId)}/session-trace`,
    { method: 'POST', body: JSON.stringify({ tenantId, sessionId, startUtc, userId, hours }) }
  );
}

// Pivot an alert into the NON-INTERACTIVE Entra sign-in feed (token replay /
// refresh-token reuse — where AiTM token theft shows up). Window is
// [startUtc - 2h, startUtc + hours] server-side. Returns
// { window, events:[{time, clientIp, asn, country, app, result, tokenType,
//   authProtocol, deviceId, riskState, correlationId, provider,
//   newForUser:{ip,asn,country}, rawEventJson}], ipSummary:[…], interactiveLegs:[…],
//   summary:{eventCount, distinctIpCount, newIpCount, foreignAsnCount, ...} }.
// alertId is decorative; tenantId/userId/startUtc carry the request.
// `identifiers` (email-only) and `entraObjectId` (a sibling GUID) fold into the
// backend's identity union alongside `userId` — the non-interactive feed records
// Teams / app token traffic under the raw object GUID, so a single-UPN filter
// under-matches the very replay this pivot exists to surface. `userId` alone still
// works (union = { userId }); pass entraObjectId when a GUID is available.
export async function postTokenUse(alertId, { tenantId, userId, identifiers, entraObjectId, startUtc, hours = 24 }) {
  const aId = alertId || 'lookup';
  return request(
    `/api/alerts/${encodeURIComponent(aId)}/token-use`,
    { method: 'POST', body: JSON.stringify({ tenantId, userId, identifiers: identifiers || [], entraObjectId: entraObjectId || '', startUtc, hours }) }
  );
}

// Re-check a Defender/AADIP pass-through alert's sign-in outcome (the `pending`
// case). Looks the correlated graph_signin up in the SilverM365GraphSignInLogs
// 14-day mirror by the exact requestId join key, classifies it with the same
// logic as the inline `_signin_outcome` stamp, persists portal-owned fields on
// the alert row, and returns either
//   { found:true, outcome:{ status, password_valid, error_code, ca_status,
//       blocked_by:[], request_id, signin_event_time }, checkedBy, checkedUtc,
//       alertRowUpdated }
// or { found:false, reason:'not_yet_mirrored'|'beyond_horizon' } (HTTP 200 — a
// not-yet-mirrored row is "retry shortly", never a hard error).
export async function postRecheckSigninOutcome(alertId, { tenantId, requestId, signinTime }) {
  const aId = alertId || 'lookup';
  return request(
    `/api/alerts/${encodeURIComponent(aId)}/recheck-signin`,
    { method: 'POST', body: JSON.stringify({ tenantId, requestId, signinTime }) }
  );
}

// "Link source audit event" for Microsoft-security pass-through alerts (e.g.
// "Mailbox Permission Change"). Correlates the alert to the Exchange admin audit
// event that carries the target mailbox / trustee / access rights and writes the
// resolved detail back onto the alert row. Returns
//   { found:true, linkConfidence:'unique', linkedAuditEvent:{…}, checkedBy, checkedUtc, alertRowUpdated }
// or { found:false, reason:'not_yet_ingested'|'ambiguous'|'title_not_supported',
//      linkConfidence, candidates? } (HTTP 200 — a not-yet-ingested / ambiguous
// result is a UI state, never a hard error). Pass chosenEventId to link a specific
// candidate after an 'ambiguous' result, or linkAll:true to link EVERY matched event
// (one alert can cover several changes — e.g. 4 mailbox-permission grants). A multi
// link returns linkConfidence:'multiple' with linkedAuditEvent = { events:[…] }.
// (handoff 2026-07-11)
export async function linkSourceAuditEvent(alertId, { tenantId, actorObjectId, actorUpn, alertTitle, winStart, winEnd, chosenEventId, linkAll }) {
  const aId = alertId || 'lookup';
  return request(
    `/api/alerts/${encodeURIComponent(aId)}/link-source-event`,
    { method: 'POST', body: JSON.stringify({ tenantId, actorObjectId, actorUpn, alertTitle, winStart, winEnd, chosenEventId, linkAll }) }
  );
}

// "Real-user evidence" for case IP classification. Grades each IP into a tier
// (1 managed / 2 registered-BYOD / 0 none) that it belongs to the real user,
// from the user's own interactive + non-interactive sign-in device binding +
// the Entra device registry. Returns
// { window, results:[{ ip, tier, tierLabel, deviceName, trustType, isCompliant,
//   registered, prtCount, appCount, days, interactiveSuccess, asn, firstSeen,
//   lastSeen, reasons:[] }], summary:{ requested, tier1, tier2, tier0 } }.
// `identifiers` (the case's email-only resolved set) UNION `entraObjectId` (a
// sibling GUID) fold into the backend's identity match alongside `userId`. The
// device-binding history keys on the sign-in UserId column, which carries the raw
// object GUID on Teams / app token rows — grading from the UPN alone ignores that
// device evidence. `userId` alone still works (union = { userId }).
export async function fetchCaseIpEvidence(caseId, { tenantId, userId, identifiers, entraObjectId, ips, days = 30 }) {
  const cId = caseId || 'lookup';
  return request(
    `/api/cases/${encodeURIComponent(cId)}/ip-evidence`,
    { method: 'POST', body: JSON.stringify({ tenantId, userId, identifiers: identifiers || [], entraObjectId: entraObjectId || '', ips: ips || [], days }) }
  );
}

// Non-interactive sign-in activity for a case (v1.147.0).
//
// `graph_signin_noninteractive` has its own Delta table and the investigation runbook
// reads only `silver_m365_unified_audit_events`, so EVERY case is blind to token
// refresh / SSO reuse / PRT replay unless this is called. AGGREGATE ONLY — distinct
// IPs and devices with counts and time bounds, never events (59 rows from 19,037 on
// the reference case).
//
// `identifiers` must be the case's resolved identifier set UNION its entraObjectId —
// `identityResolution.identifiers` is email-only and the object id is a sibling field,
// so passing the UPN alone under-matches (Teams token traffic lands under the GUID).
//
// Returns { window, coverage:{ feedFirstDate, startsAfterWindow, uncoveredDays },
//   failureCoverage:{ boundaryUtc, windowStartsBefore, failuresComparable },
//   ips:[{ ip, events, successes, withDevice, noDevice, firstSeen, lastSeen, activeDays,
//   devices[], deviceIds[], trustTypes[], tokenTypes[], userAgents[], countries[], apps[],
//   asn, provider, networkType, riskLevel, enriched }],
//   devices:[{ deviceId, name, trustType, registeredUtc, osVersion, events, ips[] }],
//   summary:{ distinctIps, distinctDevices, totalEvents, rowsCapped } }.
export async function fetchCaseNonInteractiveIps(caseId, { tenantId, identifiers, entraObjectId, startDate, endDate }) {
  const cId = caseId || 'lookup';
  return request(
    `/api/cases/${encodeURIComponent(cId)}/noninteractive-ips`,
    {
      method: 'POST',
      body: JSON.stringify({
        tenantId,
        identifiers: identifiers || [],
        entraObjectId: entraObjectId || '',
        startDate,
        endDate,
      }),
    }
  );
}

// On-demand single-IP enrichment refresh — admin-only. Synchronously calls
// ProxyCheck.io and MERGEs the result into silver_m365_unified_ip_enrichment.
// Returns the new enrichment row as JSON in alert-row field names
// (ipRiskLevel / ipCountry / ipProvider / ipDetections / ...).
// 409 on <24h-old enrichment unless `force` is set; error body carries
// `lastUpdated` so the UI can prompt for confirmation.
export async function refreshIpEnrichment(ip, { force = false, alertId, tenantId } = {}) {
  // When alertId + tenantId are supplied (real DetectionAlerts rows only — not
  // test results), the API also writes the fresh enrichment back onto that alert
  // row so it survives a page reload.
  const payload = { ip, force: !!force };
  if (alertId && tenantId) { payload.alertId = alertId; payload.tenantId = tenantId; }
  return request('/api/manage/ip-enrichment/refresh', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Batch on-demand IP enrichment — admin-only. Queries ProxyCheck.io in batches
// of <=100 for the supplied IPs, surfacing any that already carry real enrichment
// in the Delta table without a fetch. Returns
// { requested, fetched, surfaced, failed, results:[{ ipAddress, ipCountry, ipProvider,
//   ipRiskLevel, ipDetections, isCompromised, isAnonymous, city, regionName, networkType,
//   source }], errors:[{ ip, message }] }.
export async function refreshIpEnrichmentBatch(ips, { force = false } = {}) {
  return request('/api/manage/ip-enrichment/refresh-batch', {
    method: 'POST',
    body: JSON.stringify({ ips: ips || [], force: !!force }),
  });
}

// ---------------------------------------------------------------------------
// Detection Test Results Endpoints
// ---------------------------------------------------------------------------

export async function fetchTestResults(filters = {}) {
  return withMockFallback(
    async () => {
      const params = new URLSearchParams();
      if (filters.ruleId) params.set('ruleId', filters.ruleId);
      if (filters.testRunId) params.set('testRunId', filters.testRunId);
      if (filters.hours !== undefined) params.set('hours', String(filters.hours));
      if (filters.excludeTestRuns) params.set('excludeTestRuns', 'true');
      const qs = params.toString();
      const data = await request(`/api/test-results${qs ? `?${qs}` : ''}`);
      return Array.isArray(data) ? data : [];
    },
    () => mockApi.fetchTestResults(filters)
  );
}

// ---------------------------------------------------------------------------
// Detection Rule Test Run Endpoints (Admin)
// ---------------------------------------------------------------------------

export async function triggerTestRun(ruleId, params = {}) {
  return request(`/api/manage/rules/${encodeURIComponent(ruleId)}/test-run`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function pollTestRunStatus(ruleId, jobId) {
  return request(`/api/manage/rules/${encodeURIComponent(ruleId)}/test-run/${encodeURIComponent(jobId)}/status`);
}

// ---------------------------------------------------------------------------
// Detection Rule Definitions Endpoints (Admin)
// ---------------------------------------------------------------------------

export async function fetchDetectionRules() {
  return withMockFallback(
    async () => {
      const data = await request('/api/manage/rules');
      // Backend may return {rules: [...]} with etags, or a plain array
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.rules)) return data.rules;
      return [];
    },
    () => mockApi.fetchDetectionRules()
  );
}

export async function updateDetectionRule(ruleId, data, etag) {
  return withMockFallback(
    async () => {
      const headers = {};
      if (etag) headers['If-Match'] = etag;
      const result = await request(`/api/manage/rules/${encodeURIComponent(ruleId)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers,
        _returnResponse: true,
      });
      const newEtag = result.response.headers.get('etag');
      const rule = result.body?.rule || result.body;
      return { rule, etag: newEtag || result.body?.etag || null };
    },
    () => mockApi.updateDetectionRule(ruleId, data)
  );
}

export async function generateRule(inputText, inputType = 'natural_language', workload = null) {
  const payload = { inputText, inputType };
  if (workload) payload.workload = workload;
  return withMockFallback(
    () => {
      // Generate calls the separate rule builder Function App
      const baseUrl = RULEBUILDER_BASE_URL || BASE_URL;
      return request('/api/manage/rules/generate', {
        method: 'POST',
        body: JSON.stringify(payload),
        _baseUrl: baseUrl,
      });
    },
    () => mockApi.generateRule(inputText, inputType)
  );
}

export async function createRule(ruleDraft) {
  return withMockFallback(
    async () => {
      const result = await request('/api/manage/rules', {
        method: 'POST',
        body: JSON.stringify(ruleDraft),
        _returnResponse: true,
      });
      const newEtag = result.response.headers.get('etag');
      return { ...result.body, etag: newEtag || result.body?.etag || null };
    },
    () => mockApi.createRule(ruleDraft)
  );
}

export async function deleteDetectionRule(ruleId) {
  return withMockFallback(
    () => request(`/api/manage/rules/${encodeURIComponent(ruleId)}`, { method: 'DELETE' }),
    () => mockApi.deleteDetectionRule(ruleId)
  );
}

// Defender pass-through alert-title suppression (v1.101.0). Adds/removes one entry in
// the firing rule's parameters_json.excluded_alert_titles via the dedicated endpoint
// (works on PROD rules — a narrow list mutation, not a full rule edit).
export async function suppressAlertTitle(ruleId, { title, mode = 'exact' }) {
  return request(`/api/manage/rules/${encodeURIComponent(ruleId)}/excluded-alert-titles`, {
    method: 'POST',
    body: JSON.stringify({ title, mode }),
  });
}

export async function unsuppressAlertTitle(ruleId, { title, mode = 'exact' }) {
  return request(`/api/manage/rules/${encodeURIComponent(ruleId)}/excluded-alert-titles`, {
    method: 'POST',
    body: JSON.stringify({ title, mode, remove: true }),
  });
}

// Defender pass-through alert-DESCRIPTION suppression (v1.110.0). Sibling of
// suppressAlertTitle but matches on $.description with an optional detector_id gate.
// Adds/removes one entry in the firing rule's parameters_json.excluded_alert_descriptions.
export async function suppressAlertDescription(ruleId, { description, mode = 'exact', detectorId = '' }) {
  return request(`/api/manage/rules/${encodeURIComponent(ruleId)}/excluded-alert-descriptions`, {
    method: 'POST',
    body: JSON.stringify({ description, mode, detectorId }),
  });
}

export async function unsuppressAlertDescription(ruleId, { description, mode = 'exact', detectorId = '' }) {
  return request(`/api/manage/rules/${encodeURIComponent(ruleId)}/excluded-alert-descriptions`, {
    method: 'POST',
    body: JSON.stringify({ description, mode, detectorId, remove: true }),
  });
}

// Defender pass-through alert-ACTOR suppression (v1.123.2). Third sibling of
// suppressAlertTitle / suppressAlertDescription — matches on the alert's evidence actor
// (userAccount.userPrincipalName -> primaryAddress -> upn, any evidence entry), for
// Microsoft-substrate service accounts that produce non-actionable pass-through noise
// (e.g. NT SERVICE\MSExchangeAdminApiNetCore). Adds/removes one entry in the firing
// rule's parameters_json.excluded_alert_actors. Default mode 'exact' (actor strings are
// stable identifiers, unlike variable descriptions where 'contains' is the default).
export async function suppressAlertActor(ruleId, { actor, mode = 'exact' }) {
  return request(`/api/manage/rules/${encodeURIComponent(ruleId)}/excluded-alert-actors`, {
    method: 'POST',
    body: JSON.stringify({ actor, mode }),
  });
}

export async function unsuppressAlertActor(ruleId, { actor, mode = 'exact' }) {
  return request(`/api/manage/rules/${encodeURIComponent(ruleId)}/excluded-alert-actors`, {
    method: 'POST',
    body: JSON.stringify({ actor, mode, remove: true }),
  });
}

export async function probeTenantLicense(tenantId, { updateCsv = true } = {}) {
  const params = updateCsv ? '' : '?updateCsv=false';
  return withMockFallback(
    () => request(`/api/manage/tenants/${encodeURIComponent(tenantId)}/probe-license${params}`, { method: 'POST' }),
    () => mockApi.probeTenantLicense(tenantId, { updateCsv })
  );
}

export async function triggerBackfill(tenantId, { startDate, endDate, logTypes, force, windowHours }) {
  // `force` re-pulls days that already have data (the backend otherwise skips
  // already-complete day-units); `windowHours` overrides the per-day slice for a
  // clean whole-day re-pull. JSON.stringify drops undefined keys, so a normal
  // (non-forced) backfill sends neither.
  return request(`/api/manage/tenants/${encodeURIComponent(tenantId)}/backfill`, {
    method: 'POST',
    body: JSON.stringify({ startDate, endDate, logTypes, force, windowHours }),
  });
}

export async function getBackfillStatus(tenantId, jobId) {
  return request(
    `/api/manage/tenants/${encodeURIComponent(tenantId)}/backfill/${encodeURIComponent(jobId)}/status`,
    { method: 'GET' }
  );
}

// "Is a backfill running for this tenant?" — read-only, from the portal's own
// BackfillJobs tracking table. Lets the drawer re-attach on open for ANY
// authorized admin, not just the tab that happened to start the job (which is
// all the sessionStorage stash could ever cover).
export async function getActiveBackfill(tenantId) {
  return request(
    `/api/manage/tenants/${encodeURIComponent(tenantId)}/backfill/active`,
    { method: 'GET' }
  );
}

// Recover the id of a backfill the portal started but lost the handle to (the
// enqueue outran our HTTP timeout). NOT read-only: the backend has no way to be
// asked "what is active", so this attempts an enqueue and reads the single-flight
// 409 — and if there genuinely is no active job it starts one. The response says
// which happened via `recovered` / `started`; surface that to the operator.
export async function reconnectBackfill(tenantId, { startDate, endDate, logTypes }) {
  return request(`/api/manage/tenants/${encodeURIComponent(tenantId)}/backfill/reconnect`, {
    method: 'POST',
    body: JSON.stringify({ startDate, endDate, logTypes }),
  });
}

/**
 * Normalizes a case object to camelCase keys.
 * The real API returns camelCase, but mock data uses PascalCase.
 * This ensures components always receive consistent camelCase keys.
 */
function normalizeCase(data) {
  if (!data) return data;
  return {
    caseId: data.caseId ?? data.CaseID ?? data.RowKey ?? '',
    tenantId: data.tenantId ?? data.TenantID ?? data.TenantId ?? data.PartitionKey ?? '',
    tenantName: data.tenantName ?? data.TenantName ?? '',
    userPrincipalName: data.userPrincipalName ?? data.UserPrincipalName ?? data.UPN ?? '',
    status: data.status ?? data.Status ?? 'Open',
    startDate: data.startDate ?? data.StartDate ?? '',
    endDate: data.endDate ?? data.EndDate ?? '',
    iocDate: data.iocDate ?? data.IocDate ?? '',
    ticketNumber: data.ticketNumber ?? data.TicketNumber ?? '',
    notes: data.notes ?? data.Notes ?? '',
    comment: data.comment ?? data.Comment ?? '',
    knownGoodIPs: data.knownGoodIPs ?? data.KnownGoodIPs ?? '[]',
    overriddenSafeIPs: data.overriddenSafeIPs ?? data.OverriddenSafeIPs ?? '[]',
    ipClassifications: data.ipClassifications ?? data.IPClassifications ?? '{}',
    sessionClassifications: data.sessionClassifications ?? data.SessionClassifications ?? '{}',
    dismissedEventIds: data.dismissedEventIds ?? data.DismissedEventIds ?? '[]',
    // v1.189.0 — { "<eventId>": { reason, by, at } }: rows the analyst marked as attacker
    // activity. `by`/`at` are stamped by the SERVER (UpdateCase), never trusted from here.
    attackerEventIds: data.attackerEventIds ?? data.AttackerEventIds ?? '{}',
    urlClassifications: data.urlClassifications ?? data.UrlClassifications ?? '{}',
    phishingFlags: data.phishingFlags ?? data.PhishingFlags ?? '[]',
    ipEnrichmentOverlay: data.ipEnrichmentOverlay ?? data.IpEnrichmentOverlay ?? '',
    ipEvidence: data.ipEvidence ?? data.IpEvidence ?? '',
    ipComments: data.ipComments ?? data.IpComments ?? '{}',
    ualReviewed: Boolean(data.ualReviewed ?? data.UalReviewed ?? false),
    // v1.182.0 — 'access_confirmed' (default) | 'credentials_captured_no_access_found'.
    // Absent on every pre-v1.182.0 case, which reads as the default (prior behaviour).
    compromiseScope: data.compromiseScope ?? data.CompromiseScope ?? 'access_confirmed',
    nonInteractiveIps: data.nonInteractiveIps ?? data.NonInteractiveIps ?? '',
    createdBy: data.createdBy ?? data.CreatedBy ?? '',
    createdDate: data.createdDate ?? data.CreatedDate ?? '',
    lastModifiedBy: data.lastModifiedBy ?? data.LastModifiedBy ?? data.ModifiedBy ?? '',
    lastModifiedDate: data.lastModifiedDate ?? data.LastModifiedDate ?? '',
    lastRunId: data.lastRunId ?? data.LastRunId ?? '',
    lastRunDate: data.lastRunDate ?? data.LastRunDate ?? '',
    lastRunStatus: data.lastRunStatus ?? data.LastRunStatus ?? '',
  };
}

/**
 * Transforms the API response (runbook blob format) into the shape
 * the frontend components expect (PascalCase keys matching mock data).
 *
 * The API returns: { caseId, runId, metadata, results: <blob> }
 * where blob has camelCase keys from the PowerShell runbook.
 *
 * The frontend expects PascalCase keys at the top level:
 *   { SignInEvents, IPAddresses, Summary, ExchangeEvents }
 */
function normalizeResults(data) {
  if (!data) return data;

  // Unwrap: API nests blob under "results", or it might be flat
  const blob = data.results || data;

  // Transform sign-in events
  const rawEvents = blob.signInEvents || blob.SignInEvents || [];
  const SignInEvents = (Array.isArray(rawEvents) ? rawEvents : []).map(evt => ({
    ...evt,
    RiskScore: evt.RiskScore ?? evt.Risk_Total ?? 0,
    Location: evt.Location || {
      City: evt.IP_City || '',
      State: evt.IP_State || '',
      Country: evt.IP_Country || '',
    },
    RawLog: evt.RawLog || evt,
  }));

  // Transform IP address summaries (runbook uses camelCase)
  const rawIPs = blob.ipAddresses || blob.IPAddresses || [];
  const IPAddresses = (Array.isArray(rawIPs) ? rawIPs : []).map(ip => ({
    IPAddress: ip.IPAddress || ip.ipAddress || '',
    City: ip.City || ip.city || '',
    State: ip.State || ip.state || '',
    Country: ip.Country || ip.country || '',
    Provider: ip.Provider || ip.provider || ip.ISP || '',
    ISP: ip.ISP || ip.Provider || ip.provider || '',
    RiskLevel: ip.RiskLevel ?? ip.riskLevel ?? 0,
    NetworkType: ip.NetworkType || ip.networkType || ip.Type || ip.type || '',
    IsVPN: ip.IsVPN ?? ip.isVPN ?? false,
    IsProxy: ip.IsProxy ?? ip.isProxy ?? false,
    IsTOR: ip.IsTOR ?? ip.isTOR ?? false,
    IsCompromised: ip.IsCompromised ?? ip.isCompromised ?? false,
    IsAnonymous: ip.IsAnonymous ?? ip.isAnonymous ?? false,
    DRMMMatched: ip.DRMMMatched ?? ip.DRMMMatch ?? ip.drmmMatched ?? false,
    EventCount: ip.EventCount ?? ip.eventCount ?? 0,
    IsKnownGood: ip.IsKnownGood ?? ip.isKnownGood ?? false,
    AzureServiceTier: ip.AzureServiceTier || ip.azureServiceTier || '',
    IsEmailOnlyIP: ip.IsEmailOnlyIP ?? ip.isEmailOnlyIP ?? false,
  }));

  // Transform summary (runbook uses "loginsByX" / "totalSignInEvents")
  const raw = blob.summary || blob.Summary || {};
  const Summary = {
    TotalEvents: raw.TotalEvents ?? raw.totalSignInEvents ?? 0,
    HighRiskCount: raw.HighRiskCount ?? raw.highRiskEvents ?? 0,
    SuccessCount: raw.SuccessCount ?? raw.loginsByStatus?.Success ?? raw.ByStatus?.Success ?? 0,
    FailureCount: raw.FailureCount ?? raw.loginsByStatus?.Failure ?? raw.ByStatus?.Failure ?? 0,
    ByApplication: raw.ByApplication || raw.loginsByApplication || {},
    ByOS: raw.ByOS || raw.loginsByOS || {},
    ByCountry: raw.ByCountry || raw.loginsByCountry || {},
    ByCity: raw.ByCity || raw.loginsByCity || {},
    ByHour: raw.ByHour || raw.loginsByHour || {},
    ByStatus: raw.ByStatus || raw.loginsByStatus || {},
  };

  // Transform exchange events
  const rawExchange = blob.exchangeEvents || blob.ExchangeEvents || [];
  const ExchangeEvents = (Array.isArray(rawExchange) ? rawExchange : []).map(evt => demoteGuessedIP({
    ...evt,
    OriginalIPAddress: evt.OriginalIPAddress || evt.originalIPAddress || '',
    IPResolutionMethod: evt.IPResolutionMethod || evt.ipResolutionMethod || '',
    // A time-proximity GUESS (v1.194.0 / runbook 2026-10-13.1). demoteGuessedIP below puts
    // the recorded address back in IPAddress and keeps the guess here -- see utils/guessedIp.js.
    GuessedIP: evt.GuessedIP || evt.guessedIP || '',
    GuessedIPMethod: evt.GuessedIPMethod || evt.guessedIPMethod || '',
    // Where the resolution CAME from, when the method alone does not say (v1.150.0).
    // 'InCaseSessionCorrelation' = derived from this case's own sign-in + non-interactive
    // events because the pipeline's session IP map had no row for the session. Absent on
    // rows resolved from that map, and on every blob written before v1.150.0.
    IPResolutionSource: evt.IPResolutionSource || evt.ipResolutionSource || '',
    ClientAppId: evt.ClientAppId || evt.clientAppId || '',
    IsBackendService: evt.IsBackendService || evt.isBackendService || false,
    ResolvedUserIP: evt.ResolvedUserIP || evt.resolvedUserIP || '',
    ResolvedIPCount: evt.ResolvedIPCount ?? evt.resolvedIPCount ?? 0,
    AllResolvedIPs: evt.AllResolvedIPs || evt.allResolvedIPs || '',
    ServiceAppsSeen: evt.ServiceAppsSeen || evt.serviceAppsSeen || '',
    PreviousParameters: evt.PreviousParameters || evt.previousParameters || null,
    // MS-substrate app-trust classification (v1.88.0). Defaults make pre-1.88 result
    // blobs degrade gracefully — 'Unknown' is never suppressed by the MS-substrate toggle.
    AppTrustClass: evt.AppTrustClass || evt.appTrustClass || 'Unknown',
    SelfIssuedToken: evt.SelfIssuedToken || evt.selfIssuedToken || false,
    // Item / change detail (v1.151.0). Absent on every blob written before it, so
    // the defaults are what make an older case render unchanged rather than showing
    // an empty "Item / Change Detail" panel. ItemSubject and ItemAttachments are
    // SENSITIVE — see SENSITIVE_EVENT_FIELDS in utils/eventUtils.js.
    ItemSubject: evt.ItemSubject || evt.itemSubject || '',
    ItemAttachments: evt.ItemAttachments || evt.itemAttachments || '',
    ItemAttachmentCount: evt.ItemAttachmentCount ?? evt.itemAttachmentCount ?? null,
    ItemFolderPath: evt.ItemFolderPath || evt.itemFolderPath || '',
    ItemInternetMessageId: evt.ItemInternetMessageId || evt.itemInternetMessageId || '',
    ItemSizeInBytes: evt.ItemSizeInBytes ?? evt.itemSizeInBytes ?? null,
    ModifiedProperties: evt.ModifiedProperties || evt.modifiedProperties || '',
    DeviceId: evt.DeviceId || evt.deviceId || '',
    // DISPLAY-ONLY calling-app fields. ClientAppId/ClientAppName above are unchanged
    // (they feed Enterprise Apps aggregation and the app-trust classifier).
    CallingAppId: evt.CallingAppId || evt.callingAppId || '',
    CallingAppName: evt.CallingAppName || evt.callingAppName || '',
  }));

  // Transform directory audit events (general AAD operations)
  const rawDirectory = blob.directoryAuditEvents || blob.DirectoryAuditEvents || [];
  const DirectoryAuditEvents = (Array.isArray(rawDirectory) ? rawDirectory : []).map(evt => ({ ...evt }));

  // Transform SharePoint events
  const rawSharePoint = blob.sharePointEvents || blob.SharePointEvents || [];
  const SharePointEvents = (Array.isArray(rawSharePoint) ? rawSharePoint : []).map(evt => demoteGuessedIP({
    ...evt,
    OriginalIPAddress: evt.OriginalIPAddress || evt.originalIPAddress || '',
    IPResolutionMethod: evt.IPResolutionMethod || evt.ipResolutionMethod || '',
    // A time-proximity GUESS (v1.194.0 / runbook 2026-10-13.1). demoteGuessedIP below puts
    // the recorded address back in IPAddress and keeps the guess here -- see utils/guessedIp.js.
    GuessedIP: evt.GuessedIP || evt.guessedIP || '',
    GuessedIPMethod: evt.GuessedIPMethod || evt.guessedIPMethod || '',
    // Where the resolution CAME from, when the method alone does not say (v1.150.0).
    // 'InCaseSessionCorrelation' = derived from this case's own sign-in + non-interactive
    // events because the pipeline's session IP map had no row for the session. Absent on
    // rows resolved from that map, and on every blob written before v1.150.0.
    IPResolutionSource: evt.IPResolutionSource || evt.ipResolutionSource || '',
    ResolvedUserIP: evt.ResolvedUserIP || evt.resolvedUserIP || '',
    ResolvedIPCount: evt.ResolvedIPCount ?? evt.resolvedIPCount ?? 0,
    AllResolvedIPs: evt.AllResolvedIPs || evt.allResolvedIPs || '',
    ServiceAppsSeen: evt.ServiceAppsSeen || evt.serviceAppsSeen || '',
    IsBackendService: evt.IsBackendService || evt.isBackendService || false,
    Platform: evt.Platform || evt.platform || '',
    // MS-substrate app-trust classification (v1.88.0). SharePoint classifies Unknown
    // today (§3.3); ClientAppId is newly emitted by the runbook for SP events.
    ClientAppId: evt.ClientAppId || evt.clientAppId || '',
    AppTrustClass: evt.AppTrustClass || evt.appTrustClass || 'Unknown',
    SelfIssuedToken: evt.SelfIssuedToken || evt.selfIssuedToken || false,
  }));

  // Helper to flatten Azure Activity Log {value, localizedValue} enum objects to strings.
  // Azure ARM events use this nested pattern for Operation, ResourceType, Category, etc.
  const flattenValue = (v) =>
    (v && typeof v === 'object' && ('value' in v || 'localizedValue' in v))
      ? (v.localizedValue || v.value || '')
      : (v ?? '');

  // Helper to normalize the 4 inferred IP fields from camelCase or PascalCase
  const inferredFields = (evt) => ({
    InferredIP: evt.InferredIP || evt.inferredIP || '',
    InferredIPConfidence: evt.InferredIPConfidence || evt.inferredIPConfidence || '',
    InferredIPTimeDelta: evt.InferredIPTimeDelta ?? evt.inferredIPTimeDelta ?? null,
    InferredIPPairedLogType: evt.InferredIPPairedLogType || evt.inferredIPPairedLogType || '',
  });

  // Transform message trace events
  const rawMessageTrace = blob.messageTraceEvents || blob.MessageTraceEvents || [];
  const MessageTraceEvents = (Array.isArray(rawMessageTrace) ? rawMessageTrace : []).map(evt => ({ ...evt, ...inferredFields(evt) }));

  // Transform related phishing messages (from re-run with PhishingFlags)
  const rawRelatedPhishing = blob.relatedPhishingMessages || blob.RelatedPhishingMessages || [];
  const RelatedPhishingMessages = Array.isArray(rawRelatedPhishing) ? rawRelatedPhishing : [];

  // Transform URL click events
  const rawUrlClicks = blob.urlClickEvents || blob.UrlClickEvents || [];
  const UrlClickEvents = (Array.isArray(rawUrlClicks) ? rawUrlClicks : []).map(evt => ({ ...evt, ...inferredFields(evt) }));

  // Transform DLP events
  const rawDlp = blob.dlpEvents || blob.DlpEvents || [];
  const DlpEvents = (Array.isArray(rawDlp) ? rawDlp : []).map(evt => ({ ...evt, ...inferredFields(evt) }));

  // Transform General audit events
  const rawGeneral = blob.generalEvents || blob.GeneralEvents || [];
  // Teams meeting/call records are re-timed to when the activity happened (the record's own
  // time is when Microsoft wrote it, hours later) and stamped with the ids of every record of
  // their meeting -- here, once, so the attacker window, the pills, the timeline and the
  // reports all see the activity time. Original kept as RecordedTimeUTC.
  const GeneralEvents = normalizeTeamsRecords((Array.isArray(rawGeneral) ? rawGeneral : [])
    .map(evt => ({ ...evt, ...inferredFields(evt) })));

  // Transform Azure Activity events — flatten {value, localizedValue} ARM enum objects
  const rawAzureActivity = blob.azureActivityEvents || blob.AzureActivityEvents || [];
  const AzureActivityEvents = (Array.isArray(rawAzureActivity) ? rawAzureActivity : []).map(evt => ({
    ...evt,
    ...inferredFields(evt),
    Operation: flattenValue(evt.Operation),
    ResourceType: flattenValue(evt.ResourceType),
    Category: flattenValue(evt.Category),
  }));

  // Transform Risk Detection events
  const rawRiskDetection = blob.riskDetectionEvents || blob.RiskDetectionEvents || [];
  const RiskDetectionEvents = (Array.isArray(rawRiskDetection) ? rawRiskDetection : []).map(evt => ({
    ...evt,
    ...inferredFields(evt),
    Location: evt.Location || {
      City: evt.Location_City || '',
      State: evt.Location_State || '',
      Country: evt.Location_Country || '',
    },
    RequestId: evt.RequestId || evt.requestId || '',
    ResultStatus: evt.ResultStatus || evt.resultStatus || '',
  }));

  // Transform Risky User events
  const rawRiskyUser = blob.riskyUserEvents || blob.RiskyUserEvents || [];
  const RiskyUserEvents = (Array.isArray(rawRiskyUser) ? rawRiskyUser : []).map(evt => ({ ...evt }));

  // Transform Secure Score events
  const rawSecureScore = blob.secureScoreEvents || blob.SecureScoreEvents || [];
  const SecureScoreEvents = (Array.isArray(rawSecureScore) ? rawSecureScore : []).map(evt => ({ ...evt }));

  // Transform Security Alert events
  const rawSecurityAlert = blob.securityAlertEvents || blob.SecurityAlertEvents || [];
  const SecurityAlertEvents = (Array.isArray(rawSecurityAlert) ? rawSecurityAlert : []).map(evt => ({
    ...evt,
    ...inferredFields(evt),
    Title: evt.Title || evt.title || '',
    Severity: evt.Severity || evt.severity || '',
    Category: evt.Category || evt.category || '',
    Status: evt.Status || evt.status || '',
    ServiceSource: evt.ServiceSource || evt.serviceSource || '',
    Description: evt.Description || evt.description || '',
    FirstActivityDateTime: evt.FirstActivityDateTime || evt.firstActivityDateTime || '',
    LastActivityDateTime: evt.LastActivityDateTime || evt.lastActivityDateTime || '',
    EvidenceUrls: evt.EvidenceUrls || evt.evidenceUrls || '',
    EvidenceBrowser: evt.EvidenceBrowser || evt.evidenceBrowser || '',
    EvidenceOS: evt.EvidenceOS || evt.evidenceOS || '',
    AlertWebUrl: evt.AlertWebUrl || evt.alertWebUrl || '',
    MitreTechniques: evt.MitreTechniques || evt.mitreTechniques || '',
    Evidence: evt.Evidence || evt.evidence || '',
    IsUserRelated: evt.IsUserRelated ?? evt.isUserRelated ?? false,
    RequestId: evt.RequestId || evt.requestId || '',
    ResultStatus: evt.ResultStatus || evt.resultStatus || '',
  }));

  // Transform Security Incident events
  const rawSecurityIncident = blob.securityIncidentEvents || blob.SecurityIncidentEvents || [];
  const SecurityIncidentEvents = (Array.isArray(rawSecurityIncident) ? rawSecurityIncident : []).map(evt => ({
    ...evt,
    ...inferredFields(evt),
    DisplayName: evt.DisplayName || evt.displayName || '',
    Severity: evt.Severity || evt.severity || '',
    Status: evt.Status || evt.status || '',
    IsUserRelated: evt.IsUserRelated ?? evt.isUserRelated ?? false,
  }));

  // Collapsed non-interactive sign-in activity (token refresh / SSO reuse / PRT replay).
  // Each row is N authentications over a span -- NEVER a single event -- so IsAggregate
  // and the Event/Success/Failure counts are load-bearing for rendering. Its feed is a
  // separate Delta table the investigation runbook only started reading in v1.149.0, so
  // an OLDER run blob legitimately has none of this: absent must read as 'not collected',
  // never as 'no token activity'.
  // 🔴 WHETHER THE FEED WAS COLLECTED IS A DIFFERENT FACT FROM WHETHER IT HELD ANYTHING,
  // and the array cannot carry both (v1.174.0). It is defaulted to `[]` below, so a run
  // from BEFORE v1.149.0 (no such key) and a run where the feed was queried and came back
  // empty are indistinguishable downstream. `CaseScopeNotice` needs to tell them apart: on
  // the first the timeline really is incomplete, on the second it is not, and the notice
  // asserts "not included in these results" either way.
  //
  // Read from the KEY's presence, before the default is applied. A run whose
  // graph_signin_noninteractive query FAILED counts as NOT collected even though the key
  // exists — the array is empty for a reason the notice should still disclose, and
  // over-disclosing on a failure is the safe direction. (`DataSourceErrors` shows it too;
  // this is belt and braces on the one workload whose absence used to be silent.)
  const NonInteractiveCollected = nonInteractiveCollected(blob);

  const rawNonInteractive = blob.nonInteractiveSignInEvents || blob.NonInteractiveSignInEvents || [];
  const NonInteractiveSignInEvents = (Array.isArray(rawNonInteractive) ? rawNonInteractive : []).map(evt => ({
    ...evt,
    ...inferredFields(evt),
    EventCount: Number(evt.EventCount ?? evt.eventCount ?? 0),
    SuccessCount: Number(evt.SuccessCount ?? evt.successCount ?? 0),
    FailureCount: Number(evt.FailureCount ?? evt.failureCount ?? 0),
    Outcome: evt.Outcome || evt.outcome || 'Unknown',
    SessionId: evt.SessionId || evt.sessionId || '',
    // Apps is a capped LIST (8) and AppCount is the TRUE distinct total, so a truncated
    // list is disclosed rather than silently short. App is not part of the row key.
    Apps: evt.Apps || evt.apps || '',
    AppCount: Number(evt.AppCount ?? evt.appCount ?? 0),
    DeviceName: evt.DeviceName || evt.deviceName || '',
    ErrorCodes: evt.ErrorCodes || evt.errorCodes || '',
    LastEventTimeUTC: evt.LastEventTimeUTC || evt.lastEventTimeUTC || '',
    IsAggregate: true,
  }));

  // Extract run parameters (includes the actual queried date range)
  const rawParams = blob.parameters || blob.Parameters || {};
  const Parameters = {
    startDate: rawParams.startDate || rawParams.StartDate || '',
    endDate: rawParams.endDate || rawParams.EndDate || '',
  };

  // Extract user baseline (7-day behavioral normal from silver_m365_7_day_normal)
  const rawBaseline = blob.userBaseline || blob.UserBaseline || null;
  const UserBaseline = rawBaseline ? {
    snapshotDate: rawBaseline.snapshotDate || rawBaseline.SnapshotDate || null,
    windowStart: rawBaseline.windowStart || rawBaseline.WindowStart || '',
    windowEnd: rawBaseline.windowEnd || rawBaseline.WindowEnd || '',
    knownIPs: rawBaseline.knownIPs || rawBaseline.KnownIPs || [],
    osProfiles: (rawBaseline.osProfiles || rawBaseline.OsProfiles || []).map(p => ({
      os: p.os || p.OS || '',
      earliestHour: p.earliestHour ?? p.EarliestHour ?? 0,
      latestHour: p.latestHour ?? p.LatestHour ?? 23,
      signinCount: p.signinCount ?? p.SigninCount ?? 0,
      normallyCompliant: p.normallyCompliant ?? p.NormallyCompliant ?? false,
    })),
    typicalCities: rawBaseline.typicalCities || rawBaseline.TypicalCities || [],
    typicalStates: rawBaseline.typicalStates || rawBaseline.TypicalStates || [],
    typicalCountries: rawBaseline.typicalCountries || rawBaseline.TypicalCountries || [],
    topApps: rawBaseline.topApps || rawBaseline.TopApps || [],
    compliantIPs: rawBaseline.compliantIPs || rawBaseline.CompliantIPs || [],
  } : null;

  return {
    CaseID: blob.CaseID || blob.caseId || data.caseId,
    RunID: blob.RunID || blob.runId || data.runId,
    GeneratedAt: blob.GeneratedAt || blob.runTimestamp,
    Parameters,
    SignInEvents,
    ExchangeEvents,
    DirectoryAuditEvents,
    SharePointEvents,
    MessageTraceEvents,
    RelatedPhishingMessages,
    UrlClickEvents,
    DlpEvents,
    GeneralEvents,
    AzureActivityEvents,
    RiskDetectionEvents,
    RiskyUserEvents,
    SecureScoreEvents,
    SecurityAlertEvents,
    SecurityIncidentEvents,
    NonInteractiveSignInEvents,
    NonInteractiveCollected,
    enterpriseApps: (blob.enterpriseApps || blob.EnterpriseApps || []).map(app => ({
      displayName: app.displayName || app.DisplayName || '',
      appId: app.appId || app.AppId || '',
      signInEvents: app.signInEvents ?? app.signInEventCount ?? 0,
      exchangeEvents: app.exchangeEvents ?? app.exchangeEventCount ?? 0,
      totalEvents: app.totalEvents ?? app.totalEventCount ?? 0,
      ipsUsed: app.ipsUsed || app.uniqueIPs || [],
      isKnown: app.isKnown ?? app.isKnownApp ?? false,
      category: app.category || app.knownAppCategory || '',
      operations: app.operations || [],
      firstSeen: app.firstSeen || app.FirstSeen || null,
      lastSeen: app.lastSeen || app.LastSeen || null,
    })),
    IPAddresses,
    Summary,
    UserBaseline,
    // Workloads whose Databricks query failed during the run. Non-empty means the
    // affected tabs are showing MISSING data, not zero events. Older result blobs
    // predate this field and yield [].
    DataSourceErrors: (blob.dataSourceErrors || blob.DataSourceErrors || []).map(e => ({
      workload: e.workload || '',
      label: e.label || e.workload || 'Unknown workload',
      message: e.message || '',
      failedUtc: e.failedUtc || null,
    })),
    // Which directory addresses this run actually searched. Null on blobs from before
    // v1.135.0 — "not looked up", which the UI renders as nothing rather than as a
    // failed lookup.
    IdentityResolution: normalizeIdentityResolution(blob.identityResolution || blob.IdentityResolution),
  };
}
